import React, { useState, useEffect, useCallback, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  getDocs,
  serverTimestamp,
  orderBy,
  limit,
  deleteDoc,
  increment,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadString,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FaceBoomLogo, SVG_LOGO_FOR_WATERMARK } from "./svg_utilities.jsx";
import AdminPage from "./admin.jsx";
import ImageScroller from "./imageScroller.jsx";
import { AppRouter } from "./app-router.jsx";
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
  SUPPORT_STATUS_LABELS,
  SUPPORT_STATUSES,
  addSupportMessage,
  createSupportTicket,
  subscribeTicketMessages,
  subscribeUserTickets,
  updateSupportTicketStatus,
} from "./support-service.js";
import { SupportToast, statusBadgeClass } from "./support-ui.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPalette,
  faWandMagicSparkles,
  faInfinity,
  faQuestionCircle,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import {
  faDiscord,
  faFacebookF,
  faFacebookMessenger,
  faInstagram,
  faLinkedinIn,
  faRedditAlien,
  faTelegram,
  faWhatsapp,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";

// --- FIREBASE CONFIG WILL BE INJECTED HERE ---
const firebaseConfig = {
  apiKey: "AIzaSyAAzF9I2qPsiesJp0NFX6TRYzimIKlziLc",
  authDomain: "faceboom-ai-headshots.firebaseapp.com",
  projectId: "faceboom-ai-headshots",
  storageBucket: "faceboom-ai-headshots.firebasestorage.app",
  messagingSenderId: "73519928036",
  appId: "1:73519928036:web:6967919289d080786312f1",
  measurementId: "G-2Q711C9QHW",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// --- PASTE YOUR CLOUD FUNCTION URL HERE ---
// Note: It's better practice to store this in environment variables.
const cloudFunctionUrl =
  "https://us-central1-faceboom-ai-headshots.cloudfunctions.net";

// --- Logo URL ---
const logoUrl =
  "https://firebasestorage.googleapis.com/v0/b/faceboom-ai-headshots.firebasestorage.app/o/FB-LOGO-SVG.svg?alt=media&token=18ea9b8f-baca-4a07-98fa-38f98e6a1ac4";

const GUEST_SUPPORT_STORAGE_KEY = "guestSupportToken";
const CONTACT_RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000;
const CONTACT_RATE_LIMIT_MAX = 3;
const CONTACT_MIN_ELAPSED_MS = 3000;

const normalizeFirstName = (value) => {
  if (!value) return "";
  const lettersOnly = value.replace(/[^a-zA-Z]/g, "");
  if (!lettersOnly) return "";
  const trimmed = lettersOnly.slice(0, 24);
  return `${trimmed.charAt(0).toUpperCase()}${trimmed
    .slice(1)
    .toLowerCase()}`;
};

const getFirstNameFromDisplayName = (displayName = "") => {
  const firstWord = displayName.trim().split(/\s+/)[0] || "";
  return normalizeFirstName(firstWord);
};

const initialGeneratorState = {
  selectedFile: null,
  originalBase64: null,
  sourceImageUrl: null,
  processedComposite: null,
  processedMask: null,
  imagePreview: null,
  generatedImage: null,
  cleanGeneratedImage: null,
  isLoading: false,
  loadingMessage: "",
  error: null,
  clothingStyle: "",
  clothingGender: "Women's",
  timeOfDay: "Day",
  clothingFile: null,
  clothingBase64: null,
  clothingPreview: null,
  clothingOption: "original",
  aspectRatio: "4:5",
  additionalDetails: "",
  negativePrompt: "",
  styleStrength: 75,
  history: [],
  isAdvancedOpen: false,
  refinementPrompt: "",
  bodyShape: "As in photo",
  showShareOptions: false,
  savedStyles: [],
  showSaveModal: false,
  newStyleName: "",
  watermarkImage: null,
  selectedCategory: "Corporate",
  selectedSubCategory: "Modern Office",
  selectedPose: "Confident smile, arms crossed",
  selectedFraming: "Close-up",
};

function MaterialSymbol({ name, className = "", ariaHidden = true }) {
  return (
    <span
      className={`material-symbols-rounded ${className}`.trim()}
      aria-hidden={ariaHidden}
    >
      {name}
    </span>
  );
}

function AppHeader({
  user,
  firebaseUser,
  onNavigate,
  onLogout,
  supportAwaitingCount = 0,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const badgeColor =
    user?.plan === "Premium" ? "bg-yellow-500 text-gray-900" : "hidden";
  const isGoogleUser = firebaseUser?.providerData?.some(
    (provider) => provider.providerId === "google.com"
  );
  const userAvatar = isGoogleUser ? firebaseUser?.photoURL : null;

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleNavigate = (target) => {
    setIsMenuOpen(false);
    onNavigate(target);
  };

  const menuItems = [
    {
      label: "Account",
      icon: "person",
      onClick: () => handleNavigate("account"),
    },
    {
      label: "Gallery",
      icon: "photo_library",
      onClick: () => handleNavigate("gallery"),
    },
    {
      label: "Credits",
      icon: "offline_bolt",
      onClick: () => handleNavigate("pricing"),
    },
    {
      label: "Support",
      icon: "support_agent",
      badge: supportAwaitingCount,
      onClick: () => handleNavigate("support"),
    },
    ...(user?.role === "admin"
      ? [
          {
            label: "Admin Panel",
            icon: "shield_person",
            onClick: () => handleNavigate("admin"),
          },
        ]
      : []),
    {
      label: "Logout",
      icon: "logout",
      onClick: () => {
        setIsMenuOpen(false);
        setShowLogoutConfirm(true);
      },
    },
  ];

  return (
    <>
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-sm text-center text-gray-100">
            <h3 className="text-xl font-bold mb-4">Log Out?</h3>
            <p className="text-gray-400 mb-8">
              Are you sure you want to log out of your account?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="py-3 px-6 rounded-lg bg-gray-600 hover:bg-gray-500 font-semibold flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogoutConfirm(false);
                  onLogout?.();
                }}
                className="py-3 px-6 rounded-lg bg-red-600 hover:bg-red-700 font-bold text-white flex-1"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-20 bg-gray-900/80 backdrop-blur-sm border-b border-gray-700/50">
        <div className="w-full max-w-2xl mx-auto p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {user?.plan === "Premium" && (
              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${badgeColor}`}
              >
                <span className="hidden sm:inline">{user.plan}</span>
                <span className="sm:hidden">P</span>
              </span>
            )}
            <FaceBoomLogo className="h-8 w-auto" />
            <button
              type="button"
              onClick={() => onNavigate("app")}
              className="text-2xl text-white cursor-pointer focus:outline-none bg-transparent border-0 p-0"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
              }}
            >
              faceboom
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => onNavigate("pricing")}
              className="flex items-center gap-1.5 font-semibold text-sm text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full hover:bg-yellow-400/20 transition whitespace-nowrap"
            >
              <span>{user?.credits || 0}</span>
              <span className="hidden sm:inline">Credits</span>
              <MaterialSymbol
                name="offline_bolt"
                className="text-base block sm:hidden"
              />
            </button>
            {user && (
              <div className="relative">
                <button
                  type="button"
                  ref={buttonRef}
                  onClick={() => setIsMenuOpen((prev) => !prev)}
                  className="text-gray-300 hover:text-white transition p-1 rounded-full focus-visible:ring-2 focus-visible:ring-indigo-500"
                  aria-haspopup="menu"
                  aria-expanded={isMenuOpen}
                  aria-label="Open account menu"
                >
                  {userAvatar ? (
                    <img
                      src={userAvatar}
                      alt="Account avatar"
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <MaterialSymbol name="settings" className="text-2xl" />
                  )}
                </button>
                {isMenuOpen && (
                  <div
                    ref={menuRef}
                    role="menu"
                    className="absolute right-0 mt-2 w-52 rounded-lg bg-gray-800 border border-gray-700 shadow-xl overflow-hidden"
                  >
                    {menuItems.map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        role="menuitem"
                        onClick={item.onClick}
                        className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-gray-200 hover:bg-gray-700 transition"
                      >
                        <MaterialSymbol name={item.icon} className="text-lg" />
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.badge > 0 && (
                          <span className="text-xs font-semibold bg-amber-500/20 text-amber-200 border border-amber-400/40 px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

// --- Main App Component (Acts as a simple router) ---
export default function App() {
  const [page, setPage] = useState("landing");
  const [user, setUser] = useState(null); // Firebase user object
  const [userData, setUserData] = useState(null); // Firestore user data
  const [authMode, setAuthMode] = useState("login");
  const [modalContent, setModalContent] = useState(null);
  const [generatorState, setGeneratorState] = useState(initialGeneratorState);
  const [authLoading, setAuthLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [supportAwaitingCount, setSupportAwaitingCount] = useState(0);
  const [effectiveUserId, setEffectiveUserId] = useState(null);
  const [impersonatedUserId, setImpersonatedUserId] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [guestSupportToken, setGuestSupportToken] = useState(null);
  const shareIdRef = useRef(null);
  const guestSupportTokenRef = useRef(null);
  const pendingFirstNameRef = useRef("");

  const updateGeneratorState = (newState) => {
    setGeneratorState((prevState) => ({ ...prevState, ...newState }));
  };

  const createCompositeAndMask = useCallback(
    (imageBase64, targetAspectRatio) => {
      if (!imageBase64) return;
      const img = new Image();
      img.src = imageBase64;
      img.onload = () => {
        const MAX_WIDTH = 1024;
        const MAX_HEIGHT = 1024;
        let targetWidth, targetHeight;
        if (targetAspectRatio === "16:9") {
          targetWidth = MAX_WIDTH;
          targetHeight = Math.round((MAX_WIDTH * 9) / 16);
        } else {
          targetHeight = MAX_HEIGHT;
          targetWidth = Math.round((MAX_HEIGHT * 4) / 5);
        }
        const compositeCanvas = document.createElement("canvas");
        compositeCanvas.width = targetWidth;
        compositeCanvas.height = targetHeight;
        const compositeCtx = compositeCanvas.getContext("2d");
        const sourceWidth = img.width;
        const sourceHeight = img.height;
        const sourceAspectRatio = sourceWidth / sourceHeight;
        const destAspectRatio = targetWidth / targetHeight;
        let sx, sy, sWidth, sHeight;
        if (sourceAspectRatio > destAspectRatio) {
          sHeight = sourceHeight;
          sWidth = sHeight * destAspectRatio;
          sx = (sourceWidth - sWidth) / 2;
          sy = 0;
        } else {
          sWidth = sourceWidth;
          sHeight = sWidth / destAspectRatio;
          sx = 0;
          sy = (sourceHeight - sHeight) / 2;
        }
        compositeCtx.drawImage(
          img,
          sx,
          sy,
          sWidth,
          sHeight,
          0,
          0,
          targetWidth,
          targetHeight
        );

        const maskCanvas = document.createElement("canvas");
        maskCanvas.width = targetWidth;
        maskCanvas.height = targetHeight;
        const maskCtx = maskCanvas.getContext("2d");
        maskCtx.fillStyle = "white";
        maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

        updateGeneratorState({
          processedComposite: compositeCanvas.toDataURL("image/jpeg"),
          processedMask: maskCanvas.toDataURL("image/jpeg"),
        });
      };
    },
    []
  );

  useEffect(() => {
    // Dynamically load font and set favicon
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;700&display=swap";
    link.rel = "stylesheet";
    if (!document.querySelector(`link[href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
    const materialSymbolsLink = document.createElement("link");
    materialSymbolsLink.href =
      "https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,400,0,0";
    materialSymbolsLink.rel = "stylesheet";
    if (
      !document.querySelector(`link[href="${materialSymbolsLink.href}"]`)
    ) {
      document.head.appendChild(materialSymbolsLink);
    }

    const faviconLink = document.createElement("link");
    faviconLink.rel = "icon";
    faviconLink.type = "image/svg+xml";
    faviconLink.href = logoUrl;
    document.head.appendChild(faviconLink);

    const shareMatch = window.location.pathname.match(/^\/share\/([^/?#]+)/);
    const guestSupportMatch = window.location.pathname.match(
      /^\/support\/guest\/([^/?#]+)/
    );
    if (shareMatch) {
      shareIdRef.current = shareMatch[1];
      setShareId(shareMatch[1]);
      setPage("share");
    } else if (guestSupportMatch) {
      guestSupportTokenRef.current = guestSupportMatch[1];
      setGuestSupportToken(guestSupportMatch[1]);
      setPage("guest-support");
    }

    let unsubUserData = () => {}; // No-op function for cleanup

    // Firebase Auth Listener
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      unsubUserData(); // Unsubscribe from previous user's data listener

      if (currentUser) {
        const isGoogleUser = currentUser.providerData?.some(
          (provider) => provider.providerId === "google.com"
        );
        const googleFirstName = isGoogleUser
          ? getFirstNameFromDisplayName(currentUser.displayName)
          : "";
        setUser(currentUser);
        const adminDocRef = doc(db, "users", currentUser.uid);
        const adminSnapshot = await getDoc(adminDocRef);
        const adminData = adminSnapshot.exists() ? adminSnapshot.data() : null;
        const adminRole = adminData?.role === "admin";
        setIsAdminUser(adminRole);

        const storedImpersonationId =
          adminRole && typeof window !== "undefined"
            ? localStorage.getItem("admin_impersonating_id")
            : null;
        const nextEffectiveUserId =
          adminRole && storedImpersonationId
            ? storedImpersonationId
            : currentUser.uid;
        setEffectiveUserId(nextEffectiveUserId);
        setImpersonatedUserId(
          adminRole && storedImpersonationId ? storedImpersonationId : null
        );

        const userDocRef = doc(db, "users", nextEffectiveUserId);

        // Re-assign the cleanup function with the new snapshot listener
        unsubUserData = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const dbData = docSnap.data();
            // --- ROLE CHECK AND MIGRATION ---
            if (!dbData.role) {
              // User exists but has no role, migrate them
              updateDoc(userDocRef, { role: "user" }).then(() => {
                const updatedUserData = { ...dbData, role: "user" };
                setUserData(updatedUserData);
                updateGeneratorState({
                  savedStyles: updatedUserData.savedStyles || [],
                });
              });
            } else {
              // User exists with a role
              setUserData(dbData);
              updateGeneratorState({ savedStyles: dbData.savedStyles || [] });
            }
            if (
              isGoogleUser &&
              nextEffectiveUserId === currentUser.uid &&
              googleFirstName &&
              dbData.firstName !== googleFirstName
            ) {
              updateDoc(userDocRef, { firstName: googleFirstName });
            }
          } else if (nextEffectiveUserId === currentUser.uid) {
            // This is a new user, create their document
            const resolvedFirstName =
              pendingFirstNameRef.current || googleFirstName;
            const newUser = {
              email: currentUser.email,
              credits: 2,
              plan: "Free",
              savedStyles: [],
              createdAt: new Date(),
              firstName: resolvedFirstName || "",
              role: "user", // --- ADD ROLE ON CREATION ---
            };
            pendingFirstNameRef.current = "";
            setDoc(userDocRef, newUser).then(() => setUserData(newUser));
          } else {
            setUserData(null);
          }
        });

        if (shareIdRef.current) {
          setPage("share");
        } else if (guestSupportTokenRef.current) {
          setPage("guest-support");
        } else {
          setPage("app");
        }
      } else {
        setUser(null);
        setUserData(null);
        setEffectiveUserId(null);
        setImpersonatedUserId(null);
        setIsAdminUser(false);
        if (shareIdRef.current) {
          setPage("share");
        } else if (guestSupportTokenRef.current) {
          setPage("guest-support");
        } else {
          setPage("landing");
        }
      }
      // This is now called for both logged-in and logged-out states
      setAuthLoading(false);
    });

    // Create watermark image from SVG
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      updateGeneratorState({ watermarkImage: img });
    };
    const svgBlob = new Blob([SVG_LOGO_FOR_WATERMARK], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;

    // Handle modals via URL hash
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === "contact") {
        const storedToken =
          typeof window !== "undefined"
            ? localStorage.getItem(GUEST_SUPPORT_STORAGE_KEY)
            : null;
        if (storedToken) {
          guestSupportTokenRef.current = storedToken;
          setGuestSupportToken(storedToken);
          setPage("guest-support");
          setModalContent(null);
          window.history.replaceState(null, "", window.location.pathname);
          return;
        }
      }
      if (["terms", "privacy", "contact"].includes(hash)) {
        setModalContent(hash);
      } else {
        setModalContent(null);
      }
    };

    handleHashChange(); // Check on initial load
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      unsubscribeAuth(); // Cleanup auth listener
      unsubUserData(); // Cleanup user data listener
      URL.revokeObjectURL(url);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (generatorState.originalBase64) {
      createCompositeAndMask(
        generatorState.originalBase64,
        generatorState.aspectRatio
      );
    }
  }, [
    generatorState.originalBase64,
    generatorState.aspectRatio,
    createCompositeAndMask,
  ]);

  useEffect(() => {
    if (!user || !effectiveUserId) {
      setSupportAwaitingCount(0);
      return undefined;
    }
    const ticketsRef = collection(db, "support_tickets");
    const ticketsQuery = query(
      ticketsRef,
      where("userId", "==", effectiveUserId)
    );
    const unsubscribe = onSnapshot(
      ticketsQuery,
      (snapshot) => {
        const awaitingUser = snapshot.docs.filter((docSnap) => {
          const data = docSnap.data();
          return (
            data.status !== SUPPORT_STATUSES.CLOSED &&
            data.status !== SUPPORT_STATUSES.RESOLVED &&
            data.lastMessageBy === "admin"
          );
        });
        setSupportAwaitingCount(awaitingUser.length);
      },
      (error) => {
        console.error("Failed to load support ticket badge:", error);
      }
    );
    return () => unsubscribe();
  }, [effectiveUserId, user]);

  const handleAuthAction = async (email, password, firstName = "") => {
    try {
      if (authMode === "signup") {
        pendingFirstNameRef.current = normalizeFirstName(firstName);
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      console.error("Authentication Error:", error);
      alert(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      alert(error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  const handleCheckout = async (priceId, priceType) => {
    if (!user) return;
    setIsCheckingOut(true);
    const checkoutSessionRef = collection(
      db,
      "customers",
      user.uid,
      "checkout_sessions"
    );

    // Determine the checkout mode based on the price type from Stripe
    const mode = priceType === "recurring" ? "subscription" : "payment";

    try {
      const docRef = await addDoc(checkoutSessionRef, {
        price: priceId,
        mode: mode, // Add the mode here
        success_url: `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.origin,
      });

      onSnapshot(docRef, (snap) => {
        const { error, url } = snap.data();
        if (error) {
          console.error(`An error occurred: ${error.message}`);
          setIsCheckingOut(false);
          alert(`Payment Error: ${error.message}`);
        }
        if (url) {
          window.location.assign(url);
        }
      });
    } catch (error) {
      console.error("Error creating checkout session", error);
      alert("Could not initiate checkout. Please try again.");
      setIsCheckingOut(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;

    setIsCheckingOut(true);

    try {
      // Lazy-load to avoid touching your existing imports at the top of the file
      const { getFunctions, httpsCallable } = await import("firebase/functions");

      // Use the same Firebase app instance if you're already using one,
      // otherwise getFunctions() will use the default initialized app.
      const functions = getFunctions();

      const createPortalLink = httpsCallable(
        functions,
        "ext-firestore-stripe-payments-createPortalLink"
      );

      // Stripe extension expects returnUrl (camelCase)
      const result = await createPortalLink({
        returnUrl: window.location.origin,
      });

      const url = result?.data?.url;
      if (!url) {
        console.error("Portal link callable returned no url:", result?.data);
        alert("Could not open billing portal (missing URL). Please try again.");
        setIsCheckingOut(false);
        return;
      }

      window.location.assign(url);
    } catch (error) {
      console.error("Error creating portal link (callable)", error);
      alert("Could not open billing portal. Please try again.");
      setIsCheckingOut(false);
    }
  };

  // This is now only for DECREMENTING credits on the frontend for immediate feedback.
  // The actual credit grant happens on the backend.
  const handleCreditUpdate = async (creditsToDecrement) => {
    if (!user || !userData || creditsToDecrement >= 0) return;
    const newCredits = Math.max(
      0,
      (userData.credits || 0) + creditsToDecrement
    );
    setUserData((prev) => ({ ...prev, credits: newCredits }));
  };

  const closeModal = () => {
    window.location.hash = "";
  };

  const navigate = (targetPage) => {
    setPage(targetPage);
    window.scrollTo(0, 0);
  };

  const isImpersonating = isAdminUser && Boolean(impersonatedUserId);
  const handleExitImpersonation = () => {
    localStorage.removeItem("admin_impersonating_id");
    window.location.reload();
  };

  if (authLoading || isCheckingOut) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white">
        <FaceBoomLogo className="h-24 w-auto animate-pulse" />
        {isCheckingOut && (
          <p className="mt-4 text-lg">Redirecting to checkout...</p>
        )}
      </div>
    );
  }

  return (
    <>
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-2 flex flex-row items-center justify-between gap-3 whitespace-nowrap text-xs sm:text-sm shadow-lg">
          <span className="font-semibold tracking-wide flex-1 min-w-0 truncate">
            VIEWING AS {userData?.email || impersonatedUserId}
          </span>
          <button
            type="button"
            onClick={handleExitImpersonation}
            className="bg-white/10 hover:bg-white/20 text-white font-semibold px-3 py-1.5 rounded-md shrink-0"
          >
            Exit
          </button>
        </div>
      )}
      <div className={isImpersonating ? "pt-12" : ""}>
        <AppRouter
          page={page}
          navigate={navigate}
          authMode={authMode}
          setAuthMode={setAuthMode}
          userData={userData}
          firebaseUser={user}
          effectiveUserId={effectiveUserId}
          generatorState={generatorState}
          updateGeneratorState={updateGeneratorState}
          onAuthAction={handleAuthAction}
          onGoogleSignIn={handleGoogleSignIn}
          onLogout={handleLogout}
          onCheckout={handleCheckout}
          onManageSubscription={handleManageSubscription}
          onCreditUpdate={handleCreditUpdate}
          supportAwaitingCount={supportAwaitingCount}
          shareId={shareId}
          guestSupportToken={guestSupportToken}
          components={{
            LandingPage,
            AuthPage,
            GeneratorPage,
            PricingPage,
            AccountPage,
            GalleryPage,
            ShareLandingPage,
            SupportPage,
            GuestSupportPage,
            AdminPage,
          }}
        />
      </div>
      <AppFooter />
      {modalContent && <LegalModal type={modalContent} onClose={closeModal} />}
    </>
  );
}

// --- CONTACT FORM COMPONENT (for use in modal) ---
function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [replyLink, setReplyLink] = useState("");
  const formStartedAtRef = useRef(Date.now());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError("");
    setReplyLink("");
    const now = Date.now();
    const elapsedMs = now - formStartedAtRef.current;
    if (elapsedMs < CONTACT_MIN_ELAPSED_MS) {
      setSubmitError("Please wait a moment before submitting.");
      return;
    }
    if (typeof window !== "undefined") {
      let submissions = [];
      try {
        const stored = localStorage.getItem("contactFormSubmissions");
        submissions = stored ? JSON.parse(stored) : [];
      } catch (error) {
        submissions = [];
      }
      const recent = submissions.filter(
        (timestamp) => now - timestamp < CONTACT_RATE_LIMIT_WINDOW_MS
      );
      if (recent.length >= CONTACT_RATE_LIMIT_MAX) {
        setSubmitError("Too many requests. Please try again later.");
        return;
      }
      recent.push(now);
      localStorage.setItem("contactFormSubmissions", JSON.stringify(recent));
    }
    setSubmitting(true);
    try {
      const { getFunctions, httpsCallable } = await import(
        "firebase/functions"
      );
      const functions = getFunctions();
      const submitContactForm = httpsCallable(functions, "submitContactForm");
      const result = await submitContactForm({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        honeypot: honeypot.trim(),
        elapsedMs,
      });
      if (result?.data?.success) {
        setSubmitted(true);
        setReplyLink(result?.data?.replyUrl || "");
        if (typeof window !== "undefined") {
          const token = result?.data?.guestToken;
          if (token) {
            localStorage.setItem(GUEST_SUPPORT_STORAGE_KEY, token);
          }
        }
      } else {
        setSubmitError(
          result?.data?.error ||
            "We couldn't send your message. Please try again."
        );
      }
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setSubmitError(
        error?.message || "We couldn't send your message. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-green-400 mb-2">Thank you!</h2>
        <p className="text-gray-300">
          Your message has been received. We'll get back to you shortly.
        </p>
        {replyLink && (
          <div className="mt-4 text-sm text-gray-400 space-y-2">
            <p>
              Save this link to view and reply to your support conversation:
            </p>
            <a
              href={replyLink}
              className="text-indigo-400 hover:text-indigo-300 break-all"
            >
              {replyLink}
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="sr-only" aria-hidden="true">
        <label htmlFor="contact-company">Company</label>
        <input
          id="contact-company"
          type="text"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your Name"
          required
          className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="How can we help?"
          required
          rows="4"
          className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition resize-none"
        ></textarea>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold py-3 px-6 rounded-lg shadow-xl hover:from-purple-600 hover:to-indigo-700 transition transform hover:scale-105 flex items-center justify-center gap-2"
      >
        <MaterialSymbol name="outgoing_mail" className="text-xl" />
        {submitting ? "Sending..." : "Send Message"}
      </button>
      {submitError && (
        <p className="text-sm text-red-400 text-center">{submitError}</p>
      )}
    </form>
  );
}

// --- LEGAL TOOLTIP COMPONENT / FOOTER ---
function AppFooter() {
  return (
    <footer className="w-full text-center py-6 border-t border-gray-800 text-gray-400 text-sm bg-gray-900">
      {/* Top links */}
      <div className="flex justify-center gap-6 mb-4">
        <a href="#contact" className="hover:text-gray-300 transition-colors">Contact Us</a>
        <a href="#terms" className="hover:text-gray-300 transition-colors">Terms of Service</a>
        <a href="#privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</a>
      </div>

      {/* Copyright */}
      <p className="text-gray-300">&copy; {new Date().getFullYear()} Faceboom</p>

      {/* Short disclaimer (second line) */}
      <p className="mt-1 text-gray-400">Independent Project — not affiliated with Facebook®/Meta®</p>

      {/* Icon on its own line (bottom) */}
      <div className="mt-2 flex justify-center">
        <span className="relative group inline-flex">
          <button
            type="button"
            aria-label="Trademark disclaimer"
            className="rounded p-1 text-gray-500 hover:text-gray-300 focus-visible:ring-2 focus-visible:ring-indigo-400/50"
          >
            <FontAwesomeIcon icon={faQuestionCircle} className="h-4 w-4 align-middle" />
          </button>

          {/* WIDE tooltip: centered, roomy, never a skinny column */}
          <span
            role="tooltip"
            className="
              absolute bottom-8 left-1/2 -translate-x-1/2 z-50
              bg-gray-900 text-gray-100 text-sm leading-relaxed text-left
              px-4 py-3 rounded-md shadow-xl ring-1 ring-black/20
              whitespace-normal break-words
              min-w-[22rem] max-w-[42rem]            /* set a comfortable min + max width */
              sm:min-w-[26rem] md:min-w-[30rem]      /* wider on bigger screens */
              w-[90vw] sm:w-auto                     /* phone-safe width clamp */
              opacity-0 transition-opacity duration-150
              pointer-events-none
              group-hover:opacity-100 group-focus-within:opacity-100
            "
          >
            Faceboom™ is an independent project, not affiliated with Facebook® or Meta®.
            Facebook® and Meta® are registered trademarks of Meta Platforms, Inc.
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-full -translate-x-1/2 mt-[2px] h-0 w-0
                         border-l-8 border-r-8 border-t-8
                         border-l-transparent border-r-transparent border-t-gray-900"
            />
          </span>
        </span>
      </div>
    </footer>
  );
}

// --- LANDING PAGE ---
function LandingPage({ onNavigate }) {
  const allAfterImages = [
    { url: "https://i.imgur.com/CySjDwn.png", alt: "Corporate" },
    { url: "https://i.imgur.com/n4EqteV.png", alt: "Creative" },
    { url: "https://i.imgur.com/D2ROxNr.png", alt: "Tech" },
    { url: "https://i.imgur.com/0hKcKTu.png", alt: "Natural" },
    { url: "https://imgur.com/79mUg9V.png", alt: "Going Out" },
    { url: "https://imgur.com/JGDVZFP.png", alt: "Destination" },
    { url: "https://i.imgur.com/CRS3a48.png", alt: "Corporate Style" },
    { url: "https://i.imgur.com/8sjfLYo.png", alt: "Tech Start-up Style" },
    { url: "https://i.imgur.com/muIA5YZ.png", alt: "Creative Style" },
    { url: "https://i.imgur.com/mTVP7c8.png", alt: "Natural Style" },
    { url: "https://i.imgur.com/pxE5JH1.png", alt: "Modern Office" },
    { url: "https://imgur.com/BTJUfN7.png", alt: "Classic Portrait" },
    { url: "https://imgur.com/TqrnQSg.png", alt: "Team Setting" },
    { url: "https://i.imgur.com/0Dt4PdC.png", alt: "Bright & Airy Office" },
    { url: "https://i.imgur.com/RKWnVui.png", alt: "Urban Environment" },
    { url: "https://i.imgur.com/3YPc30o.png", alt: "Art Studio" },
    { url: "https://i.imgur.com/Ddxy1SV.png", alt: "Minimalist Background" },
    { url: "https://i.imgur.com/XIILJJl.png", alt: "Bold Colors" },
    { url: "https://i.imgur.com/6ZTdd78.png", alt: "Indoor (Window Light)" },
    { url: "https://i.imgur.com/ApzeuKL.png", alt: "Outdoor (Park)" },
    { url: "https://i.imgur.com/COuYErF.png", alt: "Cocktail Lounge" },
    { url: "https://i.imgur.com/WP73kAe.png", alt: "City Nightscape" },
    { url: "https://i.imgur.com/t6EYC64.png", alt: "Elegant Restaurant" },
    { url: "https://i.imgur.com/B2gTzfA.png", alt: "European City" },
    { url: "https://imgur.com/T64sgjj.png", alt: "Tropical Beach" },
    { url: "https://imgur.com/PxtctAq.png", alt: "Mountain Hike" },
    { url: "https://i.imgur.com/HMeBwmO.png", alt: "Confident pose" },
    { url: "https://i.imgur.com/0jFarwP.png", alt: "Leaning pose" },
    { url: "https://imgur.com/t0Q87xz.png", alt: "Warm smile" },
    { url: "https://imgur.com/9eOXKLg.png", alt: "Approachable laugh" },
    { url: "https://imgur.com/sFjKWt7.png", alt: "Leaning on wall" },
    { url: "https://imgur.com/d318DT7.png", alt: "Casual look" },
    { url: "https://imgur.com/fOHrBnh.png", alt: "Thoughtful expression" },
    { url: "https://imgur.com/ecoHhWD.png", alt: "Direct gaze" },
    { url: "https://imgur.com/ZEXKiSO.png", alt: "Relaxed smile" },
    { url: "https://imgur.com/S7n2daZ.png", alt: "Looking away" },
    { url: "https://imgur.com/ssNR1N7.png", alt: "Candid laughing" },
    { url: "https://i.imgur.com/HNCjDQb.png", alt: "Confident & poised" },
    { url: "https://i.imgur.com/gafqbAm.png", alt: "Candid laughing" },
    { url: "https://i.imgur.com/1Njmuxb.png", alt: "Over-the-shoulder look" },
    { url: "https://imgur.com/7ctZuhS.png", alt: "Exploring" },
    { url: "https://imgur.com/UIXKr9H.png", alt: "Smiling with landmark" },
    { url: "https://imgur.com/eEZv78D.png", alt: "Adventurous look" },
    { url: "https://i.imgur.com/XOD1Mwz.png", alt: "Close-up framing" },
    { url: "https://i.imgur.com/ngPYbmE.png", alt: "Medium shot framing" },
    { url: "https://i.imgur.com/TRK1SeD.png", alt: "Day time" },
    { url: "https://i.imgur.com/RaQqwAR.png", alt: "Golden hour" },
    { url: "https://i.imgur.com/gP2PnVQ.png", alt: "Night time" },
  ];

  const shuffleArray = (array) => {
    let currentIndex = array.length,
      randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }
    return array;
  };

  const shuffledImages = shuffleArray([...allAfterImages]);
  const uniqueAfterImages = shuffledImages.filter(
    (v, i, a) => a.findIndex((t) => t.url === v.url) === i
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
      <main className="container mx-auto px-5 md:px-6 py-10 md:py-12 text-center flex-grow">
        {/* Brand + Hero */}
        <header className="mb-10 md:mb-12">
          <div className="flex justify-center items-center gap-3 md:gap-4">
            <FaceBoomLogo className="h-10 md:h-14 w-auto" aria-hidden="true" />
            <span
              className="text-3xl md:text-5xl text-white tracking-tight"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
              }}
            >
              faceboom
            </span>
          </div>

          <h1 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            Your Perfect Portrait — Still You, Just Reimagined
          </h1>

          <p className="mt-3 text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
            Turn a simple selfie into a professional-quality headshot in
            seconds.
          </p>
        </header>

        {/* Before + arrow */}
        <section aria-label="Upload example" className="my-10 md:my-12 px-1">
          <div className="flex justify-center">
            <img
              src="https://i.imgur.com/cFrTtEk.png"
              alt="Example selfie before transformation"
              className="rounded-xl shadow-lg aspect-[4/5] object-cover h-44 sm:h-56 md:h-64 border border-gray-800"
              loading="lazy"
            />
          </div>
          <div className="flex justify-center my-5 md:my-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-gray-500 animate-bounce"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </div>
        </section>

        {/* Scrolling results */}
        <section
          aria-label="Generated portraits"
          className="mb-10 md:mb-12 space-y-6"
        >
          <ImageScroller images={uniqueAfterImages} speed="720s" />
        </section>

        {/* Value statement + CTA */}
        <section className="px-4 max-w-full md:max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold">
            <span className="sm:hidden">One photo. Every portrait.</span>
            <span className="hidden sm:inline">
              From one photo to every portrait you need
            </span>
          </h2>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mt-3">
            Business, casual, outdoors, or studio — let our AI create portraits
            tailored to every occasion.
          </p>

          <button
            onClick={() => onNavigate("auth")}
            className="mt-6 sm:mt-8 inline-flex items-center justify-center bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold sm:font-bold py-3 sm:py-4 px-7 sm:px-10 rounded-lg shadow-xl hover:from-purple-600 hover:to-indigo-700 transition-transform duration-300 hover:scale-[1.03] text-base sm:text-xl focus:outline-none focus:ring-2 focus:ring-purple-400/60"
            aria-label="Get started creating your headshot"
          >
            Get Started
          </button>
        </section>

        {/* Features */}
        <section className="mt-14 md:mt-20 grid md:grid-cols-3 gap-5 md:gap-8 max-w-5xl mx-auto px-2">
          {/* Card 1 */}
          <div className="bg-gray-800/70 backdrop-blur rounded-xl p-6 text-left">
            <div className="mb-3 flex items-center gap-3">
              <FontAwesomeIcon
                icon={faPalette}
                className="h-6 w-6 text-purple-400"
              />
              <h3 className="text-lg md:text-xl font-bold text-purple-300">
                Total Control
              </h3>
            </div>
            <p className="text-gray-300 text-sm md:text-base">
              Choose your style, pose, framing, clothing, and lighting.
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-gray-800/70 backdrop-blur rounded-xl p-6 text-left">
            <div className="mb-3 flex items-center gap-3">
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                className="h-6 w-6 text-purple-400"
              />
              <h3 className="text-lg md:text-xl font-bold text-purple-300">
                AI-Powered
              </h3>
            </div>
            <p className="text-gray-300 text-sm md:text-base">
              Our advanced AI generates brand-new, photorealistic portraits.
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-gray-800/70 backdrop-blur rounded-xl p-6 text-left">
            <div className="mb-3 flex items-center gap-3">
              <FontAwesomeIcon
                icon={faInfinity}
                className="h-6 w-6 text-purple-400"
              />
              <h3 className="text-lg md:text-xl font-bold text-purple-300">
                Endless Options
              </h3>
            </div>
            <p className="text-gray-300 text-sm md:text-base">
              Perfect for corporate headshots, social media profiles, and more.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

// --- AUTHENTICATION PAGE ---
function AuthPage({ onAuthAction, onGoogleSignIn, mode, setMode, onNavigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === "signup") {
      const normalizedFirstName = normalizeFirstName(firstName);
      if (!normalizedFirstName) {
        setFirstNameError(
          "Enter a first name using letters only (max 24 characters)."
        );
        return;
      }
      onAuthAction(email, password, normalizedFirstName);
      return;
    }
    onAuthAction(email, password);
  };
  const isLogin = mode === "login";

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md flex-grow flex flex-col justify-center">
        <header className="text-center mb-8">
          <div className="flex justify-center items-center gap-4">
            <FaceBoomLogo className="h-12 md:h-16 w-auto" />
            <span
              className="text-4xl md:text-5xl text-white"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
              }}
            >
              faceboom
            </span>
          </div>
          <p className="text-xl text-gray-400 mt-4">
            Create your perfect portrait!
          </p>
        </header>
        <div className="bg-gray-800 p-8 rounded-2xl shadow-2xl relative">
          <button
            onClick={() => onNavigate("landing")}
            className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors"
            title="Back to Home"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-3xl font-bold text-center mb-2">
            {isLogin ? "Welcome Back!" : "Create Your Account"}
          </h2>
          <p className="text-center text-gray-400 mb-6">
            {isLogin
              ? "Sign in to continue."
              : "Get started with 2 free credits."}
          </p>
          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(normalizeFirstName(e.target.value));
                    if (firstNameError) setFirstNameError("");
                  }}
                  placeholder="First name"
                  required
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                />
                {firstNameError && (
                  <p className="text-xs text-red-400 mt-2">
                    {firstNameError}
                  </p>
                )}
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold py-3 px-6 rounded-lg shadow-xl hover:from-purple-600 hover:to-indigo-700 transition transform hover:scale-105"
            >
              {isLogin ? "Log In" : "Sign Up"}
            </button>
          </form>
          <div className="my-6 flex items-center">
            <div className="flex-grow border-t border-gray-600"></div>
            <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
            <div className="flex-grow border-t border-gray-600"></div>
          </div>
          <button
            onClick={onGoogleSignIn}
            className="w-full bg-white text-gray-700 font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-gray-100 transition transform hover:scale-105 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48">
              <path
                fill="#FFC107"
                d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
              ></path>
              <path
                fill="#FF3D00"
                d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
              ></path>
              <path
                fill="#4CAF50"
                d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.519-3.317-11.28-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
              ></path>
              <path
                fill="#1976D2"
                d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C39.999,35.596,44,29.825,44,24C44,22.659,43.862,21.35,43.611,20.083z"
              ></path>
            </svg>
            {isLogin ? "Log In with Google" : "Sign Up with Google"}
          </button>
          <p className="text-center text-sm text-gray-400 mt-6">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              onClick={() => setMode(isLogin ? "signup" : "login")}
              className="font-semibold text-indigo-400 hover:text-indigo-300 ml-2"
            >
              {isLogin ? "Sign Up" : "Log In"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

// --- PRICING PAGE ---
function PricingPage({
  user,
  onCheckout,
  onNavigate,
  onLogout,
  firebaseUser,
  supportAwaitingCount,
}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const getDisplayPrice = (prices) =>
    prices.find((price) => price.active) || prices[0];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const productsRef = collection(db, "products");
      const q = query(productsRef, where("active", "==", true));
      const querySnapshot = await getDocs(q);

      const fetchedProducts = [];
      for (const prodDoc of querySnapshot.docs) {
        const productData = { id: prodDoc.id, ...prodDoc.data() };
        const pricesRef = collection(db, "products", prodDoc.id, "prices");
        const pricesSnap = await getDocs(pricesRef);
        const fetchedPrices = pricesSnap.docs.map((priceDoc) => ({
          id: priceDoc.id,
          ...priceDoc.data(),
        }));
        const activePrices = fetchedPrices.filter((price) => price.active);
        productData.prices = (activePrices.length ? activePrices : fetchedPrices).sort(
          (a, b) => a.unit_amount - b.unit_amount
        );
        fetchedProducts.push(productData);
      }

      // Sort products by price
      fetchedProducts.sort(
        (a, b) =>
          getDisplayPrice(a.prices)?.unit_amount -
          getDisplayPrice(b.prices)?.unit_amount
      );
      setProducts(fetchedProducts);
      setLoading(false);
    };

    fetchProducts();
  }, []);

  const premiumPlan = products.find(
    (p) => getDisplayPrice(p.prices)?.type === "recurring"
  );
  const creditPacks = products.filter(
    (p) => getDisplayPrice(p.prices)?.type === "one_time"
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        Loading pricing...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
      <AppHeader
        user={user}
        firebaseUser={firebaseUser}
        onNavigate={onNavigate}
        onLogout={onLogout}
        supportAwaitingCount={supportAwaitingCount}
      />
      <div className="container mx-auto p-6 flex-grow">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mt-4">
            Top up your credits
          </h1>
          <p className="text-lg text-gray-400 mt-2">
            One-time purchases. No subscription required. Credits never expire.
          </p>
        </header>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {creditPacks.map((pack, index) => {
            const price = getDisplayPrice(pack.prices);
            const credits = pack.metadata.credits_to_grant || 0;
            const headshotEstimate = Math.max(1, Math.floor(credits / 1));
            // A simple way to highlight the second-most expensive pack as "best value"
            const isPopular = index === creditPacks.length - 2;

            return (
              <div
                key={pack.id}
                className={`bg-gray-800 p-6 rounded-2xl shadow-lg border-2 text-center flex flex-col justify-between transition-transform duration-300 ${
                  isPopular
                    ? "border-purple-500 relative z-10 scale-105 md:scale-110 ring-2 ring-purple-400/70 shadow-[0_0_35px_rgba(168,85,247,0.55)] before:content-[''] before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-br before:from-purple-500/30 before:via-purple-500/10 before:to-transparent before:opacity-80 before:animate-pulse before:pointer-events-none"
                    : "border-gray-700"
                }`}
              >
                <div>
                  {isPopular && (
                    <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                      BEST VALUE
                    </span>
                  )}
                  <p className="text-4xl font-bold text-yellow-400">
                    {credits}
                  </p>
                  <p className="font-semibold mb-1">Credits</p>
                  <p className="text-xs text-gray-400 mb-2">
                    ≈ {headshotEstimate} headshots
                  </p>
                  {pack.metadata.bonus_credits && (
                    <p className="text-sm text-green-400 mb-4">
                      (Includes {pack.metadata.bonus_credits} Bonus!)
                    </p>
                  )}
                </div>
                <button
                  onClick={() => onCheckout(price.id, price.type)}
                  className="bg-indigo-600 hover:bg-indigo-700 w-full py-3 rounded-lg font-bold transition-transform transform hover:scale-105 mt-4"
                >
                  Buy for ${(price.unit_amount / 100).toFixed(2)}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-4">
            Premium vs Credits
          </h2>
          <div className="overflow-hidden rounded-2xl border border-gray-700">
            <div className="grid grid-cols-3 bg-gray-800 text-sm font-semibold">
              <div className="p-4 text-gray-300">Feature</div>
              <div className="p-4 text-purple-300 text-center">Premium</div>
              <div className="p-4 text-gray-300 text-center">Credits</div>
            </div>
            {[
              {
                label: "Priority generation",
                premium: "Included",
                credits: "Standard queue",
              },
              {
                label: "No watermark",
                premium: "Included",
                credits: "Watermark on free",
              },
              {
                label: "Refine & edit tools",
                premium: "Included",
                credits: "Premium only",
              },
              {
                label: "One-time purchases",
                premium: "Optional",
                credits: "Included",
              },
            ].map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-3 border-t border-gray-700 text-sm"
              >
                <div className="p-4 text-gray-300">{row.label}</div>
                <div className="p-4 text-center text-gray-100">
                  {row.premium}
                </div>
                <div className="p-4 text-center text-gray-400">
                  {row.credits}
                </div>
              </div>
            ))}
          </div>
        </div>

        {premiumPlan && (
          <>
            <div className="my-16 border-t border-gray-700"></div>
            <div className="max-w-3xl mx-auto text-center">
              {user?.plan === "Premium" ? (
                <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 p-8 rounded-2xl border-2 border-purple-500">
                  <h2 className="text-3xl font-bold text-yellow-400">
                    You are a Premium Member!
                  </h2>
                  <p className="text-lg text-gray-300 mt-2">
                    You have access to all premium features, including no
                    watermarks and priority generation.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-3xl font-bold">
                    Or, Go Premium for the Best Experience
                  </h2>
                  <div className="mt-6 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 p-8 rounded-2xl shadow-lg border-2 border-purple-500 flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1 text-left">
                      <h3 className="text-2xl font-bold">{premiumPlan.name}</h3>
                      <p className="text-4xl font-bold my-2">
                        ${(
                          getDisplayPrice(premiumPlan.prices).unit_amount / 100
                        ).toFixed(2)}
                        <span className="text-lg font-medium text-gray-400">
                          /mo
                        </span>
                      </p>
                      <p className="text-sm text-purple-200 font-semibold">
                        Best for ongoing creators
                      </p>
                      <ul className="mt-4 space-y-2 text-gray-300">
                        {premiumPlan.description.split(". ").map((feature) => (
                          <li key={feature} className="flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-3 text-green-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() =>
                          onCheckout(
                            getDisplayPrice(premiumPlan.prices).id,
                            getDisplayPrice(premiumPlan.prices).type
                          )
                        }
                        className="bg-purple-600 hover:bg-purple-700 w-full py-3 px-8 rounded-lg font-bold transition-transform transform hover:scale-105"
                      >
                        Subscribe Now
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
        <div className="text-center mt-12">
          <button
            onClick={() => onNavigate("app")}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            &larr; Back to Generator
          </button>
        </div>
      </div>
    </div>
  );
}

// --- ACCOUNT PAGE ---
function AccountPage({
  user,
  onNavigate,
  onManageSubscription,
  onLogout,
  firebaseUser,
  effectiveUserId,
  supportAwaitingCount,
}) {
  const [usageData, setUsageData] = useState([]);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  useEffect(() => {
    const loadUsage = async () => {
      if (!effectiveUserId) {
        setUsageLoading(false);
        return;
      }
      try {
        const formatUsageKey = (date) =>
          date.toLocaleDateString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        const usageMap = new Map();
        for (let i = 0; i < 30; i += 1) {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          const key = formatUsageKey(date);
          usageMap.set(key, 0);
        }

        const galleryRef = collection(
          db,
          "users",
          effectiveUserId,
          "gallery"
        );
        const q = query(galleryRef, orderBy("createdAt", "desc"), limit(500));
        const snapshot = await getDocs(q);
        snapshot.docs.forEach((doc) => {
          const rawCreatedAt =
            doc.data().createdAt ??
            doc.data().created ??
            doc.data().created_at;
          const createdAt =
            rawCreatedAt?.toDate?.() ??
            (rawCreatedAt?.seconds
              ? new Date(rawCreatedAt.seconds * 1000)
              : typeof rawCreatedAt === "number"
                ? new Date(
                    rawCreatedAt > 1000000000000
                      ? rawCreatedAt
                      : rawCreatedAt * 1000
                  )
                : typeof rawCreatedAt === "string"
                  ? new Date(rawCreatedAt)
                  : null);
          if (!createdAt) return;
          if (createdAt < startDate || createdAt > today) return;
          const key = formatUsageKey(createdAt);
          if (usageMap.has(key)) {
            usageMap.set(key, usageMap.get(key) + 1);
          }
        });

        const formatted = Array.from(usageMap.entries()).map(
          ([date, count]) => ({
            date,
            count,
          })
        );
        setUsageData(formatted);
      } catch (error) {
        console.error("Error loading usage data:", error);
        setUsageError("Usage history is unavailable right now.");
      } finally {
        setUsageLoading(false);
      }
    };

    loadUsage();
  }, [effectiveUserId]);

  useEffect(() => {
    if (!effectiveUserId) {
      setTransactions([]);
      setTransactionsLoading(false);
      return;
    }

    setTransactionsLoading(true);
    const paymentsRef = collection(
      db,
      "customers",
      effectiveUserId,
      "payments"
    );
    const paymentsQuery = query(paymentsRef, orderBy("created", "desc"));
    const unsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
      const paymentData = snapshot.docs.map((doc) => ({
        id: doc.data()?.id ?? doc.id,
        ...doc.data(),
      }));
      setTransactions(paymentData);
      setTransactionsLoading(false);
    });

    return () => unsubscribe();
  }, [effectiveUserId]);

  const maxUsage = usageData.reduce(
    (max, item) => Math.max(max, item.count),
    1
  );
  const totalUsage = usageData.reduce((total, item) => total + item.count, 0);
  const formatDate = (created) => {
    if (!created) return "—";
    const date =
      typeof created === "number"
        ? new Date(created * 1000)
        : created?.seconds
          ? new Date(created.seconds * 1000)
          : created?.toDate
            ? created.toDate()
            : new Date(created);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };
  const formatAmount = (amount) => {
    if (typeof amount !== "number") return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount / 100);
  };
  const statusBadgeClass = (status) => {
    if (status === "succeeded") {
      return "bg-green-500/20 text-green-300 border-green-500/40";
    }
    if (status === "processing" || status === "pending") {
      return "bg-yellow-500/20 text-yellow-300 border-yellow-500/40";
    }
    return "bg-red-500/20 text-red-300 border-red-500/40";
  };
  const getTransactionDescription = (transaction) => {
    const itemDescription = transaction?.items?.[0]?.description;
    return itemDescription ?? transaction?.description ?? "—";
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
      <AppHeader
        user={user}
        firebaseUser={firebaseUser}
        onNavigate={onNavigate}
        onLogout={onLogout}
        supportAwaitingCount={supportAwaitingCount}
      />
      <div className="container mx-auto p-6 flex-grow max-w-4xl">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold mt-4">My Account</h1>
        </header>
        <div className="bg-gray-800 p-8 rounded-2xl shadow-lg space-y-6">
          <div>
            <label className="text-sm font-medium text-gray-400">Name</label>
            <p className="text-lg">
              {user?.firstName ? user.firstName : "Not provided"}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-400">Email</label>
            <p className="text-lg">{user?.email || "Not logged in"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-400">
              Current Plan
            </label>
            <div className="flex items-center gap-3 mt-1">
              <span
                className={`text-lg font-semibold px-3 py-1 rounded-full text-sm ${
                  user?.plan === "Premium"
                    ? "bg-yellow-500 text-gray-900"
                    : "bg-gray-600 text-gray-200"
                }`}
              >
                {user?.plan || "Free"}
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-400">
              Remaining Credits
            </label>
            <p className="text-lg font-bold text-yellow-400">
              {user?.credits || 0}
            </p>
          </div>
          <div className="pt-6 border-t border-gray-700 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => onNavigate("pricing")}
              className="bg-indigo-600 hover:bg-indigo-500 w-full sm:w-auto flex-1 text-white font-bold py-3 px-6 rounded-lg shadow-xl transition-transform transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <MaterialSymbol name="offline_bolt" className="text-xl" />
              Get More Credits
            </button>
            {user?.plan === "Premium" && (
              <button
                onClick={onManageSubscription}
                className="bg-gray-600 hover:bg-gray-500 w-full sm:w-auto flex-1 text-white font-bold py-3 px-6 rounded-lg shadow-xl transition flex items-center justify-center gap-2"
              >
                <MaterialSymbol name="contract_edit" className="text-xl" />
                Manage Subscription
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 bg-gray-800 p-6 rounded-2xl shadow-lg">
          <h2 className="text-lg font-semibold mb-4">Usage (last 30 days)</h2>
          {usageLoading && (
            <p className="text-sm text-gray-400">Loading usage history...</p>
          )}
          {usageError && (
            <p className="text-sm text-red-400">{usageError}</p>
          )}
          {!usageLoading && !usageError && totalUsage > 0 && (
            <>
              <div className="flex items-end gap-1 h-32">
                {usageData.map((item) => (
                  <div
                    key={item.date}
                    className="flex-1 flex flex-col items-center justify-end gap-1"
                  >
                    <div
                      className="w-full rounded-sm bg-purple-500/70"
                      style={{
                        height: `${Math.max(
                          8,
                          (item.count / maxUsage) * 100
                        )}%`,
                      }}
                      title={`${item.count} credits used`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span>30 days ago</span>
                <span>Today</span>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Each bar represents credits used per day.
              </p>
            </>
          )}
          {!usageLoading && !usageError && totalUsage === 0 && (
            <p className="text-sm text-gray-400">
              No usage history yet. Generate a portrait to see activity here.
            </p>
          )}
        </div>

        <div className="mt-8 bg-gray-800 p-6 rounded-2xl shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <MaterialSymbol name="receipt_long" className="text-xl" />
            <h2 className="text-lg font-semibold">Transaction History</h2>
          </div>
          {transactionsLoading && (
            <p className="text-sm text-gray-400">Loading transactions...</p>
          )}
          {!transactionsLoading && transactions.length === 0 && (
            <p className="text-sm text-gray-400">No transactions yet.</p>
          )}
          {!transactionsLoading && transactions.length > 0 && (
            <>
              <div className="space-y-4 md:hidden">
                {transactions.map((transaction) => (
                  <div
                    key={`${transaction.id}-${transaction.created}`}
                    className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Date</p>
                      <p className="text-sm">{formatDate(transaction.created)}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Description</p>
                      <p className="text-sm text-right">
                        {getTransactionDescription(transaction)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Amount</p>
                      <p className="text-sm font-semibold text-white">
                        {formatAmount(transaction.amount)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Status</p>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full border ${statusBadgeClass(
                          transaction.status
                        )}`}
                      >
                        {transaction.status || "unknown"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-400">Reference</p>
                      <p className="text-sm font-mono">
                        {(transaction.id || "—").slice(-8)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-400">
                    <tr className="border-b border-gray-700">
                      <th className="py-3 px-2 font-semibold">Date</th>
                      <th className="py-3 px-2 font-semibold">Reference ID</th>
                      <th className="py-3 px-2 font-semibold">Description</th>
                      <th className="py-3 px-2 font-semibold">Amount</th>
                      <th className="py-3 px-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr
                        key={`${transaction.id}-${transaction.created}`}
                        className="border-b border-gray-800 last:border-b-0"
                      >
                        <td className="py-3 px-2">
                          {formatDate(transaction.created)}
                        </td>
                        <td className="py-3 px-2 font-mono text-xs text-gray-300">
                          {(transaction.id || "—").slice(-8)}
                        </td>
                        <td className="py-3 px-2">
                          {getTransactionDescription(transaction)}
                        </td>
                        <td className="py-3 px-2 font-semibold">
                          {formatAmount(transaction.amount)}
                        </td>
                        <td className="py-3 px-2">
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full border ${statusBadgeClass(
                              transaction.status
                            )}`}
                          >
                            {transaction.status || "unknown"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => onNavigate("app")}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            &larr; Back to Generator
          </button>
        </div>
      </div>
    </div>
  );
}

// --- GALLERY PAGE ---
function GalleryPage({
  user,
  onNavigate,
  onLogout,
  firebaseUser,
  effectiveUserId,
  generatorState,
  updateGeneratorState,
  onCreditUpdate,
  supportAwaitingCount,
}) {
  const [galleryImages, setGalleryImages] = useState([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(true);
  const [galleryError, setGalleryError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [shareModalImage, setShareModalImage] = useState(null);
  const [shareShortUrl, setShareShortUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [refineOpenId, setRefineOpenId] = useState(null);
  const [refineInputs, setRefineInputs] = useState({});
  const [refineLoadingId, setRefineLoadingId] = useState(null);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [refineError, setRefineError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);
  const isPremium = user?.plan === "Premium";

  useEffect(() => {
    if (!effectiveUserId) {
      setIsLoadingGallery(false);
      return;
    }

    const galleryCollectionRef = collection(
      db,
      "users",
      effectiveUserId,
      "gallery"
    );
    const q = query(galleryCollectionRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const images = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setGalleryImages(images);
        setIsLoadingGallery(false);
      },
      (error) => {
        console.error("Error fetching gallery:", error);
        setGalleryError("Could not load your gallery. Please try again later.");
        setIsLoadingGallery(false);
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId]);

  const handleDelete = async () => {
    if (!imageToDelete || !effectiveUserId) return;
    const { id, imageUrl, shortId } = imageToDelete;

    try {
      const docRef = doc(db, "users", effectiveUserId, "gallery", id);
      await deleteDoc(docRef);
      if (shortId) {
        const sharedRef = doc(db, "shared_links", shortId);
        await deleteDoc(sharedRef);
      }
      const storageRef = ref(storage, imageUrl);
      await deleteObject(storageRef);
    } catch (error) {
      console.error("Error deleting image: ", error);
      alert("Failed to delete image. Please try again.");
    } finally {
      setImageToDelete(null);
      setSelectedImage(null);
    }
  };

  const handleDownload = (imageUrl, event) => {
    event?.stopPropagation();
    if (!imageUrl) return;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      const newWindow = window.open(imageUrl, "_blank");
      if (newWindow) newWindow.focus();
      else alert("Please allow popups for this site to save your image.");
    } else {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = "faceboom-headshot.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const ensurePremium = (event) => {
    if (isPremium) return false;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setShowUpgradeModal(true);
    return true;
  };

  const callGenerateImage = async (payload) => {
    if (!firebaseUser) {
      setRefineError("You must be logged in to perform this action.");
      return null;
    }
    let attempt = 0;
    const maxAttempts = 3;
    const initialDelay = 1000;
    while (attempt < maxAttempts) {
      try {
        const token = await firebaseUser.getIdToken();
        const response = await fetch(`${cloudFunctionUrl}/generateImage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error ||
              `API responded with status: ${response.status}`
          );
        }
        const result = await response.json();
        return result;
      } catch (err) {
        attempt++;
        if (attempt >= maxAttempts) {
          setRefineError(
            err.message ||
              "Failed to communicate with the server after multiple attempts."
          );
          return null;
        }
        await delay(initialDelay * Math.pow(2, attempt));
      }
    }
    return null;
  };

  const saveImageToGallery = async (imageB64) => {
    if (!effectiveUserId || !imageB64) return null;
    try {
      const storageRef = ref(
        storage,
        `user_uploads/${effectiveUserId}/gallery/${Date.now()}.png`
      );
      const snapshot = await uploadString(storageRef, imageB64, "data_url");
      const downloadURL = await getDownloadURL(snapshot.ref);

      const galleryCollectionRef = collection(
        db,
        "users",
        effectiveUserId,
        "gallery"
      );
      const docRef = await addDoc(galleryCollectionRef, {
        imageUrl: downloadURL,
        imagePath: storageRef.fullPath,
        createdAt: serverTimestamp(),
        favorite: false,
      });
      return {
        id: docRef.id,
        imageUrl: downloadURL,
        imagePath: storageRef.fullPath,
        createdAt: new Date(),
        favorite: false,
      };
    } catch (error) {
      console.error("Error saving image to gallery:", error);
      return null;
    }
  };

  const handleRefineClick = (event, image) => {
    if (ensurePremium(event)) return;
    setRefineOpenId((prev) => (prev === image.id ? null : image.id));
  };

  const handleRefineSubmit = async (event, image) => {
    if (ensurePremium(event)) return;
    const promptText = (refineInputs[image.id] || "").trim();
    if (!promptText || refineLoadingId) return;
    if ((user?.credits || 0) < 1) {
      onNavigate("pricing");
      return;
    }

    setRefineError(null);
    setRefineLoadingId(image.id);
    try {
      const payload = {
        isRefinement: true,
        refinementPrompt: promptText,
        source_image_url: image.imageUrl,
      };
      const result = await callGenerateImage(payload);
      if (result && result.imageUrl) {
        const rawImage = result.imageUrl;
        setSelectedImage((prev) =>
          prev && prev.id === image.id ? { ...prev, imageUrl: rawImage } : prev
        );
        const savedImage = await saveImageToGallery(rawImage);
        if (savedImage) {
          setSelectedImage(savedImage);
          setRefineOpenId(savedImage.id);
          setGalleryImages((prev) => {
            if (prev.find((entry) => entry.id === savedImage.id)) return prev;
            return [savedImage, ...prev];
          });
        }
        if (updateGeneratorState) {
          const newEntry = { clean: rawImage, display: rawImage };
          updateGeneratorState({
            history: [newEntry, ...generatorState.history].slice(0, 5),
          });
        }
        if (onCreditUpdate) {
          onCreditUpdate(-1);
        }
        setRefineInputs((prev) => ({ ...prev, [image.id]: "" }));
      }
    } catch (error) {
      console.error("Error refining image:", error);
      setRefineError("Failed to refine image. Please try again.");
    } finally {
      setRefineLoadingId(null);
    }
  };

  const handleEdit = async (event, image) => {
    if (ensurePremium(event)) return;
    setEditLoadingId(image.id);
    try {
      updateGeneratorState({
        ...initialGeneratorState,
        sourceImageUrl: image.imageUrl,
        imagePreview: image.imageUrl,
        selectedFile: null,
      });
      onNavigate("app");
    } catch (error) {
      console.error("Error opening image in generator:", error);
      setRefineError("Failed to open image in the generator.");
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleFavoriteToggle = async (event, image) => {
    event?.stopPropagation();
    if (!effectiveUserId) return;
    try {
      const docRef = doc(db, "users", effectiveUserId, "gallery", image.id);
      await updateDoc(docRef, { favorite: !image.favorite });
    } catch (error) {
      console.error("Error updating favorite:", error);
    }
  };

  const handleCompareToggle = (event, image) => {
    event?.stopPropagation();
    setCompareSelection((prev) => {
      if (prev.find((item) => item.id === image.id)) {
        return prev.filter((item) => item.id !== image.id);
      }
      if (prev.length >= 4) return prev;
      return [...prev, image];
    });
  };

  const extractStoragePath = (url) => {
    if (!url || typeof url !== "string") return "";
    const encodedPath = url.split("/o/")[1]?.split("?")[0];
    if (!encodedPath) return "";
    return decodeURIComponent(encodedPath);
  };

  const generateShortId = () =>
    Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);

  const ensureShareLink = async (image) => {
    if (!effectiveUserId || !image?.imageUrl) return null;
    const imagePath = image.imagePath || extractStoragePath(image.imageUrl);
    let shortId = image.shortId;

    if (!shortId) {
      let attempts = 0;
      while (attempts < 5) {
        const candidate = generateShortId();
        const existing = await getDoc(doc(db, "shared_links", candidate));
        if (!existing.exists()) {
          shortId = candidate;
          break;
        }
        attempts += 1;
      }
      if (!shortId) throw new Error("Unable to generate a share link.");
    }

    const sharedRef = doc(db, "shared_links", shortId);
    const sharedSnap = await getDoc(sharedRef);
    if (!sharedSnap.exists()) {
      await setDoc(sharedRef, {
        shortId,
        imageUrl: image.imageUrl,
        originalOwner: effectiveUserId,
        imagePath: imagePath || null,
        createdAt: serverTimestamp(),
      });
    }

    const galleryRef = doc(
      db,
      "users",
      effectiveUserId,
      "gallery",
      image.id
    );
    await updateDoc(galleryRef, { shortId });
    setGalleryImages((prev) =>
      prev.map((entry) =>
        entry.id === image.id ? { ...entry, shortId } : entry
      )
    );

    return {
      shortId,
      shareUrl: `${window.location.origin}/share/${shortId}`,
    };
  };

  const openShareModal = async (event, image) => {
    event?.stopPropagation();
    setShareModalImage(image);
    setShareShortUrl("");
    setShareError(null);
    setShareLoading(true);
    try {
      const shareData = await ensureShareLink(image);
      if (shareData?.shareUrl) {
        setShareShortUrl(shareData.shareUrl);
      }
    } catch (error) {
      console.error("Error creating share link:", error);
      setShareError("Unable to create a share link. Please try again.");
    } finally {
      setShareLoading(false);
    }
  };

  const closeShareModal = () => {
    setShareModalImage(null);
    setShareShortUrl("");
    setShareError(null);
    setShareLoading(false);
  };

  const handleCopyShareLink = async () => {
    if (!shareShortUrl) return;
    try {
      await navigator.clipboard.writeText(shareShortUrl);
    } catch (error) {
      console.error("Failed to copy share link:", error);
    }
  };

  const shareMessage = "Check out my new AI portrait from faceboom!";
  const sharePlatforms = shareShortUrl
    ? [
        {
          name: "X/Twitter",
          icon: faXTwitter,
          href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            shareMessage
          )}&url=${encodeURIComponent(shareShortUrl)}`,
        },
        {
          name: "Facebook",
          icon: faFacebookF,
          href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            shareShortUrl
          )}`,
        },
        {
          name: "LinkedIn",
          icon: faLinkedinIn,
          href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
            shareShortUrl
          )}&title=${encodeURIComponent("My New AI Portrait")}&summary=${encodeURIComponent(
            shareMessage
          )}`,
        },
        {
          name: "Discord",
          icon: faDiscord,
          href: "https://discord.com/channels/@me",
        },
        {
          name: "Facebook Messenger",
          icon: faFacebookMessenger,
          href: `fb-messenger://share?link=${encodeURIComponent(shareShortUrl)}`,
        },
        {
          name: "WhatsApp",
          icon: faWhatsapp,
          href: `https://wa.me/?text=${encodeURIComponent(
            `${shareMessage} ${shareShortUrl}`
          )}`,
        },
        {
          name: "Instagram",
          icon: faInstagram,
          href: `https://www.instagram.com/?url=${encodeURIComponent(
            shareShortUrl
          )}`,
        },
        {
          name: "Telegram",
          icon: faTelegram,
          href: `https://t.me/share/url?url=${encodeURIComponent(
            shareShortUrl
          )}&text=${encodeURIComponent(shareMessage)}`,
        },
        {
          name: "Reddit",
          icon: faRedditAlien,
          href: `https://www.reddit.com/submit?url=${encodeURIComponent(
            shareShortUrl
          )}&title=${encodeURIComponent("My New AI Portrait")}`,
        },
        {
          name: "Email",
          icon: faEnvelope,
          href: `mailto:?subject=${encodeURIComponent(
            "My New AI Portrait"
          )}&body=${encodeURIComponent(`${shareMessage}\n\n${shareShortUrl}`)}`,
        },
      ]
    : [];

  const filteredImages = galleryImages.filter((image) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "favorites") return image.favorite;
    const category = image.selectedCategory || "";
    const subCategory = image.selectedSubCategory || "";
    const combined = `${category} ${subCategory}`.toLowerCase();
    if (activeFilter === "professional") {
      return ["corporate", "tech start-up"].includes(category.toLowerCase());
    }
    if (activeFilter === "outdoor") {
      return [
        "outdoor",
        "park",
        "beach",
        "mountain",
        "city",
        "urban",
      ].some((keyword) => combined.includes(keyword));
    }
    if (activeFilter === "studio") {
      return ["studio", "portrait", "office"].some((keyword) =>
        combined.includes(keyword)
      );
    }
    return true;
  });

  const renderGallery = () => {
    if (isLoadingGallery)
      return (
        <div className="text-center text-gray-400">Loading your gallery...</div>
      );
    if (galleryError)
      return <div className="text-center text-red-400">{galleryError}</div>;
    if (galleryImages.length === 0) {
      return (
        <div className="text-center text-gray-500 py-8 border-2 border-dashed border-gray-700 rounded-lg">
          <p className="font-semibold">Your gallery is empty.</p>
          <p className="text-sm">Generated headshots will appear here.</p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: "All" },
              { id: "professional", label: "Most professional" },
              { id: "outdoor", label: "Outdoor" },
              { id: "studio", label: "Studio" },
              { id: "favorites", label: "Favorites" },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  activeFilter === filter.id
                    ? "bg-purple-600 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setCompareMode((prev) => !prev);
                setCompareSelection([]);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                compareMode
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-700 text-gray-200 hover:bg-gray-600"
              }`}
            >
              {compareMode ? "Exit Compare" : "Compare"}
            </button>
            {compareMode && (
              <span className="text-xs text-gray-400">
                Select 2–4 images
              </span>
            )}
          </div>
        </div>

        {filteredImages.length === 0 ? (
          <div className="text-center text-gray-500 py-8 border-2 border-dashed border-gray-700 rounded-lg">
            <p className="font-semibold">No matches for this filter.</p>
            <p className="text-sm">Try a different filter or reset to All.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {filteredImages.map((image) => (
              <div key={image.id} className="relative group">
                <button
                  type="button"
                  className={`aspect-[4/5] w-full cursor-pointer rounded-lg overflow-hidden border-2 transition ${
                    compareSelection.find((item) => item.id === image.id)
                      ? "border-purple-500"
                      : "border-transparent"
                  }`}
                  onClick={() =>
                    compareMode
                      ? handleCompareToggle(null, image)
                      : setSelectedImage(image)
                  }
                >
                  <img
                    src={image.imageUrl}
                    alt="Generated headshot"
                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                  />
                </button>
                <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={(event) => handleDownload(image.imageUrl, event)}
                    className="bg-gray-900/80 text-white p-2 rounded-full hover:bg-gray-700"
                    title="Download"
                  >
                    <MaterialSymbol name="download" className="text-base" />
                  </button>
                  <button
                    onClick={(event) => handleFavoriteToggle(event, image)}
                    className={`p-2 rounded-full ${
                      image.favorite
                        ? "bg-yellow-500 text-gray-900"
                        : "bg-gray-900/80 text-white hover:bg-gray-700"
                    }`}
                    title="Favorite"
                  >
                    <MaterialSymbol name="star" className="text-base" />
                  </button>
                  <button
                    onClick={(event) => openShareModal(event, image)}
                    className="bg-gray-900/80 text-white p-2 rounded-full hover:bg-gray-700"
                    title="Share"
                  >
                    <MaterialSymbol name="share" className="text-base" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {compareMode && (
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-200">
                Compare selected
              </h3>
              <button
                onClick={() => setCompareSelection([])}
                className="text-xs text-gray-400 hover:text-gray-200"
              >
                Clear selection
              </button>
            </div>
            {compareSelection.length < 2 ? (
              <p className="text-sm text-gray-400">
                Select at least two images to compare side-by-side.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {compareSelection.map((image) => (
                  <div key={image.id} className="space-y-2">
                    <img
                      src={image.imageUrl}
                      alt="Selected comparison"
                      className="rounded-lg object-cover w-full aspect-[4/5]"
                    />
                    <button
                      onClick={(event) => handleCompareToggle(event, image)}
                      className="w-full text-xs text-gray-300 bg-gray-700 hover:bg-gray-600 py-1 rounded-lg"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative w-[min(92vw,1100px)] h-[90vh] max-h-[90vh] bg-gray-900 rounded-lg shadow-2xl p-4 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-4 -right-4 bg-white text-gray-800 rounded-full p-1.5 z-10 hover:scale-110 transition-transform"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
              <img
                src={selectedImage.imageUrl}
                alt="Enlarged headshot"
                className="w-full h-full max-w-full max-h-full object-contain rounded-md block"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-4 relative shrink-0">
              <div className="relative group">
                <button
                  onClick={(event) => handleRefineClick(event, selectedImage)}
                  className={`font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center ${
                    isPremium
                      ? "bg-indigo-600 hover:bg-indigo-500 text-white"
                      : "bg-gray-700 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Refine
                </button>
                {!isPremium && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-900 text-yellow-300 text-xs font-bold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Premium feature
                  </div>
                )}
              </div>
              <div className="relative group">
                <button
                  onClick={(event) => handleEdit(event, selectedImage)}
                  className={`font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center ${
                    isPremium
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-700 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {editLoadingId === selectedImage.id ? "Loading..." : "Edit"}
                </button>
                {!isPremium && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-900 text-yellow-300 text-xs font-bold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Premium feature
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDownload(selectedImage.imageUrl)}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Save
              </button>
              <button
                onClick={(event) => openShareModal(event, selectedImage)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                  />
                </svg>
                Share
              </button>
              <button
                onClick={() => setImageToDelete(selectedImage)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete
              </button>
            </div>
            {refineOpenId === selectedImage.id && (
              <div className="space-y-2 rounded-lg bg-gray-800 p-4 max-h-[30vh] overflow-y-auto shrink-0">
                <textarea
                  value={refineInputs[selectedImage.id] || ""}
                  onChange={(event) =>
                    setRefineInputs((prev) => ({
                      ...prev,
                      [selectedImage.id]: event.target.value,
                    }))
                  }
                  placeholder="Describe the changes…"
                  className="w-full p-3 bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300 h-24 resize-none text-sm"
                />
                <button
                  onClick={(event) => handleRefineSubmit(event, selectedImage)}
                  disabled={
                    refineLoadingId === selectedImage.id ||
                    !(refineInputs[selectedImage.id] || "").trim()
                  }
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {refineLoadingId === selectedImage.id
                    ? "Refining..."
                    : "Generate refinement"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-sm text-center text-gray-100">
            <h3 className="text-xl font-bold mb-4">Delete Image?</h3>
            <p className="text-gray-400 mb-8">
              This action is permanent and cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setImageToDelete(null)}
                className="py-3 px-6 rounded-lg bg-gray-600 hover:bg-gray-500 font-semibold flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="py-3 px-6 rounded-lg bg-red-600 hover:bg-red-700 font-bold text-white flex-1"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {shareModalImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeShareModal}
        >
          <div
            className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-6 text-gray-100"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Share your headshot</h3>
              <button
                type="button"
                onClick={closeShareModal}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="w-full aspect-[4/5] bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={shareModalImage.imageUrl}
                alt="Share preview"
                className="w-full h-full object-cover"
              />
            </div>
            {shareLoading && (
              <p className="text-sm text-gray-400 text-center">
                Preparing your share link...
              </p>
            )}
            {shareError && (
              <p className="text-sm text-red-400 text-center">{shareError}</p>
            )}
            {shareShortUrl && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareShortUrl}
                    className="flex-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200"
                  />
                  <button
                    type="button"
                    onClick={handleCopyShareLink}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-semibold"
                  >
                    Copy Link
                  </button>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  {sharePlatforms.map((platform) => {
                    const isExternal = platform.href.startsWith("http");
                    return (
                      <a
                        key={platform.name}
                        href={platform.href}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        className="group relative flex h-11 w-11 items-center justify-center rounded-full bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-white transition"
                        aria-label={`Share on ${platform.name}`}
                        title={platform.name}
                      >
                        <FontAwesomeIcon
                          icon={platform.icon}
                          className="text-lg"
                        />
                        <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-gray-200 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                          {platform.name}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-sm text-center text-gray-100">
            <h3 className="text-xl font-bold mb-4">Premium Feature</h3>
            <p className="text-gray-400 mb-8">
              Upgrade to Premium to refine and edit gallery images.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="py-3 px-6 rounded-lg bg-gray-600 hover:bg-gray-500 font-semibold flex-1"
              >
                Not Now
              </button>
              <button
                onClick={() => onNavigate("pricing")}
                className="py-3 px-6 rounded-lg bg-yellow-500 hover:bg-yellow-400 font-bold text-gray-900 flex-1"
              >
                Upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
        <AppHeader
          user={user}
          firebaseUser={firebaseUser}
          onNavigate={onNavigate}
          onLogout={onLogout}
          supportAwaitingCount={supportAwaitingCount}
        />
        <div className="container mx-auto p-6 flex-grow max-w-4xl">
          <header className="text-center mb-12">
            <h1 className="text-4xl font-bold mt-4">My Gallery</h1>
          </header>
          {refineError && (
            <p className="text-red-400 text-center mb-4">{refineError}</p>
          )}
          {renderGallery()}

          <div className="text-center mt-12">
            <button
              onClick={() => onNavigate("app")}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              &larr; Back to Generator
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// --- SHARE LANDING PAGE ---
function ShareLandingPage({ shareId, onNavigate }) {
  const [shareData, setShareData] = useState(null);
  const [shareLoading, setShareLoading] = useState(true);
  const [shareError, setShareError] = useState(null);

  useEffect(() => {
    const loadSharedImage = async () => {
      if (!shareId) {
        setShareError("Invalid share link.");
        setShareLoading(false);
        return;
      }
      try {
        const shareRef = doc(db, "shared_links", shareId);
        const shareSnap = await getDoc(shareRef);
        if (!shareSnap.exists()) {
          setShareError("This shared image is no longer available.");
        } else {
          setShareData(shareSnap.data());
        }
      } catch (error) {
        console.error("Failed to load shared image:", error);
        setShareError("Unable to load this shared image.");
      } finally {
        setShareLoading(false);
      }
    };

    loadSharedImage();
  }, [shareId]);

  const handleCtaClick = () => {
    onNavigate?.("landing");
    window.location.assign("/");
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-4xl w-full text-center space-y-6">
        <h1 className="text-3xl md:text-4xl font-bold">
          AI Headshot Preview
        </h1>
        <p className="text-gray-400">
          See the shared FaceBoom AI headshot below.
        </p>

        {shareLoading && (
          <p className="text-gray-400">Loading shared image...</p>
        )}
        {shareError && (
          <p className="text-red-400 font-semibold">{shareError}</p>
        )}
        {shareData?.imageUrl && (
          <div className="bg-gray-900/70 border border-gray-800 rounded-2xl p-6 flex flex-col items-center gap-6">
            <div className="w-full max-w-md aspect-[4/5] rounded-xl overflow-hidden shadow-xl">
              <img
                src={shareData.imageUrl}
                alt="Shared AI headshot"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-3">
              <h2 className="text-2xl font-semibold">
                Create your own AI headshot today!
              </h2>
              <p className="text-gray-400">
                Turn a single selfie into polished headshots in minutes.
              </p>
              <button
                type="button"
                onClick={handleCtaClick}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-semibold"
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- SUPPORT PAGE ---
function SupportPage({
  user,
  onNavigate,
  onLogout,
  firebaseUser,
  effectiveUserId,
  supportAwaitingCount,
}) {
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [messageBody, setMessageBody] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const createFormRef = useRef(null);

  useEffect(() => {
    if (!effectiveUserId) {
      setTickets([]);
      setTicketsLoading(false);
      return undefined;
    }
    setTicketsLoading(true);
    const unsubscribe = subscribeUserTickets({
      db,
      userId: effectiveUserId,
      onData: (snapshot) => {
        const nextTickets = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setTickets(nextTickets);
        if (nextTickets.length > 0) {
          setSelectedTicketId((current) => current ?? nextTickets[0].id);
        }
        setTicketsLoading(false);
      },
      onError: (error) => {
        console.error("Failed to load support tickets:", error);
        setTicketsError("Unable to load support tickets.");
        setTicketsLoading(false);
      },
    });
    return () => unsubscribe();
  }, [effectiveUserId]);

  useEffect(() => {
    if (!selectedTicketId) {
      setMessages([]);
      return undefined;
    }
    setMessagesLoading(true);
    const unsubscribe = subscribeTicketMessages({
      db,
      ticketId: selectedTicketId,
      onData: (snapshot) => {
        setMessages(
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
        setMessagesLoading(false);
      },
      onError: (error) => {
        console.error("Failed to load support messages:", error);
        setMessagesError("Unable to load ticket messages.");
        setMessagesLoading(false);
      },
    });
    return () => unsubscribe();
  }, [selectedTicketId]);

  const getDisplayStatus = (ticket) => {
    if (!ticket) return SUPPORT_STATUSES.OPEN;
    if (
      ticket.status === SUPPORT_STATUSES.RESOLVED ||
      ticket.status === SUPPORT_STATUSES.CLOSED
    ) {
      return ticket.status;
    }
    if (ticket.lastMessageBy === "admin") {
      return SUPPORT_STATUSES.AWAITING_USER;
    }
    if (ticket.lastMessageBy === "user") {
      return SUPPORT_STATUSES.AWAITING_SUPPORT;
    }
    return ticket.status || SUPPORT_STATUSES.OPEN;
  };

  const selectedTicket = tickets.find((ticket) => ticket.id === selectedTicketId);
  const selectedDisplayStatus = getDisplayStatus(selectedTicket);

  const formatTimestamp = (timestamp) => {
    const date = timestamp?.toDate?.();
    return date ? date.toLocaleString() : "—";
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    if (!effectiveUserId) return;
    if (!subject.trim() || !messageBody.trim()) {
      setToast({ tone: "error", message: "Subject and message are required." });
      return;
    }
    setIsCreating(true);
    try {
        const ticketId = await createSupportTicket({
          db,
          userId: effectiveUserId,
          userEmail: user?.email,
          userDisplayName: user?.firstName || user?.displayName || user?.name,
          subject: subject.trim(),
          category,
          priority,
        message: messageBody.trim(),
      });
      setSubject("");
      setCategory("");
      setPriority("Normal");
      setMessageBody("");
      setSelectedTicketId(ticketId);
      setToast({ tone: "success", message: "Support ticket created." });
    } catch (error) {
      console.error("Failed to create support ticket:", error);
      setToast({ tone: "error", message: "Failed to create ticket." });
    } finally {
      setIsCreating(false);
    }
  };

  const handleReply = async () => {
    if (!effectiveUserId || !selectedTicket) return;
    if (!replyBody.trim()) {
      setToast({ tone: "error", message: "Reply cannot be empty." });
      return;
    }
    setIsReplying(true);
    try {
      await addSupportMessage({
        db,
        ticketId: selectedTicket.id,
        authorType: "user",
        authorId: effectiveUserId,
        body: replyBody.trim(),
      });
      setReplyBody("");
      setToast({ tone: "success", message: "Reply sent to support." });
    } catch (error) {
      console.error("Failed to send reply:", error);
      setToast({ tone: "error", message: "Failed to send reply." });
    } finally {
      setIsReplying(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!effectiveUserId || !selectedTicket) return;
    setStatusLoading(true);
    try {
      await updateSupportTicketStatus({
        db,
        ticketId: selectedTicket.id,
        status,
        actorType: "user",
      });
      setToast({ tone: "success", message: "Ticket status updated." });
    } catch (error) {
      console.error("Failed to update ticket status:", error);
      setToast({ tone: "error", message: "Unable to update status." });
    } finally {
      setStatusLoading(false);
    }
  };

  if (!effectiveUserId) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
        <AppHeader
          user={user}
          firebaseUser={firebaseUser}
          onNavigate={onNavigate}
          onLogout={onLogout}
          supportAwaitingCount={supportAwaitingCount}
        />
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="max-w-lg bg-gray-800 p-8 rounded-2xl shadow-lg">
            <h1 className="text-3xl font-bold mb-4">Support</h1>
            <p className="text-gray-400 mb-6">
              Please sign in to create and manage your support tickets.
            </p>
            <button
              onClick={() => onNavigate("auth")}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2 px-6 rounded-lg"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
      <AppHeader
        user={user}
        firebaseUser={firebaseUser}
        onNavigate={onNavigate}
        onLogout={onLogout}
        supportAwaitingCount={supportAwaitingCount}
      />
      <div className="container mx-auto p-6 flex-grow max-w-6xl space-y-10">
        <header className="text-center">
          <h1 className="text-4xl font-bold mt-4">Support</h1>
          <p className="text-gray-400 mt-2">
            Need help? Open a ticket and our team will get back to you.
          </p>
        </header>

        <section
          ref={createFormRef}
          className="bg-gray-800 p-8 rounded-2xl shadow-lg space-y-6"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Create a new ticket</h2>
            {supportAwaitingCount > 0 && (
              <span className="text-xs font-semibold bg-amber-500/20 text-amber-200 border border-amber-400/40 px-2.5 py-1 rounded-full">
                {supportAwaitingCount} awaiting your reply
              </span>
            )}
          </div>
          <form onSubmit={handleCreateTicket} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Brief summary of your request"
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                >
                  <option value="">Select a category (optional)</option>
                  {SUPPORT_CATEGORIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(event) => setPriority(event.target.value)}
                  className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                >
                  {SUPPORT_PRIORITIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                value={messageBody}
                onChange={(event) => setMessageBody(event.target.value)}
                placeholder="Describe your issue in detail"
                rows={5}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isCreating}
              className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center gap-2"
            >
              <MaterialSymbol name="support_agent" className="text-lg" />
              {isCreating ? "Submitting..." : "Submit ticket"}
            </button>
          </form>
        </section>

        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Your tickets</h2>
            {ticketsLoading && (
              <span className="text-sm text-gray-400">Loading tickets...</span>
            )}
          </div>
          {ticketsError && (
            <p className="text-sm text-red-400">{ticketsError}</p>
          )}
          {!ticketsLoading && tickets.length === 0 && (
            <div className="bg-gray-800 p-8 rounded-2xl shadow-lg text-center">
              <p className="text-gray-400 mb-4">
                No tickets yet. Create one and we will respond ASAP.
              </p>
              <button
                type="button"
                onClick={() =>
                  createFormRef.current?.scrollIntoView({ behavior: "smooth" })
                }
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2 px-5 rounded-lg"
              >
                Create ticket
              </button>
            </div>
          )}

          {tickets.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-gray-800 p-4 rounded-2xl shadow-lg space-y-3">
                {tickets.map((ticket) => {
                  const displayStatus = getDisplayStatus(ticket);
                  return (
                    <button
                      key={ticket.id}
                      type="button"
                      onClick={() => setSelectedTicketId(ticket.id)}
                      className={`w-full text-left p-4 rounded-xl border transition ${
                        selectedTicketId === ticket.id
                          ? "border-indigo-500 bg-gray-900/80"
                          : "border-transparent bg-gray-900/40 hover:border-gray-600"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-semibold truncate">
                          {ticket.subject}
                        </h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(
                            displayStatus
                          )}`}
                        >
                          {SUPPORT_STATUS_LABELS[displayStatus] || "Open"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 space-y-1">
                        <div>Created: {formatTimestamp(ticket.createdAt)}</div>
                        <div>Updated: {formatTimestamp(ticket.updatedAt)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="lg:col-span-2 bg-gray-800 p-6 rounded-2xl shadow-lg space-y-6">
                {!selectedTicket && (
                  <p className="text-gray-400">
                    Select a ticket to view details and replies.
                  </p>
                )}
                {selectedTicket && (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-semibold">
                          {selectedTicket.subject}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mt-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(
                              selectedDisplayStatus
                            )}`}
                          >
                            {SUPPORT_STATUS_LABELS[selectedDisplayStatus] ||
                              "Open"}
                          </span>
                          {selectedTicket.category && (
                            <span>Category: {selectedTicket.category}</span>
                          )}
                          {selectedTicket.priority && (
                            <span>Priority: {selectedTicket.priority}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {![SUPPORT_STATUSES.CLOSED, SUPPORT_STATUSES.RESOLVED].includes(
                          selectedTicket.status
                        ) && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStatusUpdate(SUPPORT_STATUSES.RESOLVED)}
                              disabled={statusLoading}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                            >
                              Mark resolved
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusUpdate(SUPPORT_STATUSES.CLOSED)}
                              disabled={statusLoading}
                              className="bg-gray-600 hover:bg-gray-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                            >
                              Close ticket
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {messagesLoading && (
                        <p className="text-sm text-gray-400">Loading messages...</p>
                      )}
                      {messagesError && (
                        <p className="text-sm text-red-400">{messagesError}</p>
                      )}
                      {!messagesLoading &&
                        messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`p-4 rounded-xl border ${
                              msg.authorType === "user"
                                ? "bg-indigo-500/10 border-indigo-400/40"
                                : "bg-gray-900/50 border-gray-700"
                            }`}
                          >
                            <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                              <span className="uppercase tracking-wide">
                                {msg.authorType === "user" ? "You" : "Support"}
                              </span>
                              <span>{formatTimestamp(msg.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-200 whitespace-pre-wrap">
                              {msg.body}
                            </p>
                          </div>
                        ))}
                      {!messagesLoading && messages.length === 0 && (
                        <p className="text-sm text-gray-400">
                          No messages yet. Start the conversation above.
                        </p>
                      )}
                    </div>

                    {![SUPPORT_STATUSES.CLOSED, SUPPORT_STATUSES.RESOLVED].includes(
                      selectedTicket.status
                    ) && (
                      <div className="border-t border-gray-700 pt-4 space-y-3">
                        <label className="text-sm font-medium text-gray-300">
                          Add a reply
                        </label>
                        <textarea
                          value={replyBody}
                          onChange={(event) => setReplyBody(event.target.value)}
                          rows={4}
                          className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                          placeholder="Type your reply"
                        />
                        <button
                          type="button"
                          onClick={handleReply}
                          disabled={isReplying}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
                        >
                          {isReplying ? "Sending..." : "Send reply"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
      <SupportToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// --- GUEST SUPPORT PAGE ---
function GuestSupportPage({ token, onNavigate }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const loadThread = useCallback(async () => {
    if (!token) {
      setError("Invalid support link.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { getFunctions, httpsCallable } = await import(
        "firebase/functions"
      );
      const functions = getFunctions();
      const getGuestSupportThread = httpsCallable(
        functions,
        "getGuestSupportThread"
      );
      const result = await getGuestSupportThread({ token });
      if (!result?.data?.success) {
        setError(result?.data?.error || "Unable to load this support thread.");
        setTicket(null);
        setMessages([]);
      } else {
        setTicket(result?.data?.ticket || null);
        setMessages(result?.data?.messages || []);
      }
    } catch (err) {
      console.error("Failed to load guest support thread:", err);
      setError("Unable to load this support thread.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && typeof window !== "undefined") {
      localStorage.setItem(GUEST_SUPPORT_STORAGE_KEY, token);
    }
    loadThread();
  }, [loadThread, token]);

  const handleReply = async () => {
    if (!replyBody.trim() || sending) return;
    setSending(true);
    try {
      const { getFunctions, httpsCallable } = await import(
        "firebase/functions"
      );
      const functions = getFunctions();
      const postGuestSupportReply = httpsCallable(
        functions,
        "postGuestSupportReply"
      );
      const result = await postGuestSupportReply({
        token,
        message: replyBody.trim(),
      });
      if (!result?.data?.success) {
        throw new Error(result?.data?.error || "Reply failed.");
      }
      setReplyBody("");
      setToast({ tone: "success", message: "Reply sent to support." });
      await loadThread();
    } catch (err) {
      console.error("Failed to send guest reply:", err);
      setToast({ tone: "error", message: "Unable to send reply." });
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">Support</h1>
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  localStorage.removeItem(GUEST_SUPPORT_STORAGE_KEY);
                  window.location.hash = "#contact";
                }
                onNavigate?.("landing");
              }}
              className="text-gray-400 hover:text-white"
            >
              Start new request
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.("landing")}
              className="text-gray-400 hover:text-white"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 flex-grow max-w-4xl space-y-6">
        <header className="text-center space-y-2">
          <h2 className="text-3xl font-bold">Your Support Conversation</h2>
          <p className="text-gray-400">
            Reply here and our team will follow up in this thread.
          </p>
        </header>

        {loading && (
          <div className="text-center text-gray-400">Loading thread...</div>
        )}
        {error && <p className="text-red-400 text-center">{error}</p>}

        {!loading && ticket && (
          <>
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-xl font-semibold">
                  {ticket.subject || "Support request"}
                </h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(
                    ticket.status || SUPPORT_STATUSES.OPEN
                  )}`}
                >
                  {SUPPORT_STATUS_LABELS[ticket.status] || "Open"}
                </span>
              </div>
              <p className="text-sm text-gray-400">
                Ticket ID: {ticket.id}
              </p>
            </div>

            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg space-y-4">
              <h4 className="text-lg font-semibold">Messages</h4>
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-xl border ${
                      msg.authorType === "user"
                        ? "bg-indigo-500/10 border-indigo-400/40"
                        : "bg-gray-900/50 border-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                      <span className="uppercase tracking-wide">
                        {msg.authorType === "user" ? "You" : "Support"}
                      </span>
                      <span>{formatTimestamp(msg.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">
                      {msg.body}
                    </p>
                  </div>
                ))}
                {messages.length === 0 && (
                  <p className="text-sm text-gray-400">
                    No messages yet. Send a reply to get started.
                  </p>
                )}
              </div>

              {![SUPPORT_STATUSES.CLOSED, SUPPORT_STATUSES.RESOLVED].includes(
                ticket.status
              ) && (
                <div className="border-t border-gray-700 pt-4 space-y-3">
                  <label className="text-sm font-medium text-gray-300">
                    Add a reply
                  </label>
                  <textarea
                    value={replyBody}
                    onChange={(event) => setReplyBody(event.target.value)}
                    rows={4}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                    placeholder="Type your reply"
                  />
                  <button
                    type="button"
                    onClick={handleReply}
                    disabled={sending}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
                  >
                    {sending ? "Sending..." : "Send reply"}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <SupportToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// --- Generator Page Component ---
function GeneratorPage({
  user,
  onLogout,
  onNavigate,
  onCreditUpdate,
  generatorState,
  updateGeneratorState,
  firebaseUser,
  effectiveUserId,
  supportAwaitingCount,
}) {
  const [openStep, setOpenStep] = useState(1);
  const stepRefs = useRef({});
  const {
    selectedFile,
    originalBase64,
    sourceImageUrl,
    processedComposite,
    processedMask,
    imagePreview,
    generatedImage,
    cleanGeneratedImage,
    isLoading,
    loadingMessage,
    error,
    clothingStyle,
    clothingGender,
    timeOfDay,
    clothingFile,
    clothingBase64,
    clothingPreview,
    clothingOption,
    aspectRatio,
    additionalDetails,
    negativePrompt,
    styleStrength,
    history,
    isAdvancedOpen,
    refinementPrompt,
    bodyShape,
    showShareOptions,
    savedStyles,
    showSaveModal,
    newStyleName,
    watermarkImage,
    selectedCategory,
    selectedSubCategory,
    selectedPose,
    selectedFraming,
  } = generatorState;
  const isPremium = user?.plan === "Premium";
  const activeUserId = effectiveUserId || firebaseUser?.uid;

  const scrollToStep = useCallback((step) => {
    const node = stepRefs.current[step];
    if (!node) return;
    window.requestAnimationFrame(() => {
      const stickyHeader = document.querySelector("header.sticky");
      const headerOffset = stickyHeader
        ? stickyHeader.getBoundingClientRect().height
        : 0;
      const top =
        node.getBoundingClientRect().top + window.scrollY - headerOffset - 4;
      window.scrollTo({ top, behavior: "smooth" });
    });
  }, []);

  // This data can be moved to a separate config file for easier management
  const styleImages = {
    Corporate: "https://i.imgur.com/CRS3a48.png",
    "Tech Start-up": "https://i.imgur.com/8sjfLYo.png",
    Creative: "https://i.imgur.com/muIA5YZ.png",
    Natural: "https://i.imgur.com/mTVP7c8.png",
    "Going Out": "https://imgur.com/79mUg9V.png",
    Destination: "https://imgur.com/JGDVZFP.png",
  };
  const styleOptions = {
    Corporate: ["Modern Office", "Classic Portrait", "Team Setting"],
    "Tech Start-up": [
      "Bright & Airy Office",
      "Urban Environment",
      "Casual Co-working",
    ],
    Creative: ["Art Studio", "Minimalist Background", "Bold Colors"],
    Natural: ["Indoor (Window Light)", "Outdoor (Park)", "Outdoor (Urban)"],
    "Going Out": ["Cocktail Lounge", "City Nightscape", "Elegant Restaurant"],
    Destination: ["European City", "Tropical Beach", "Mountain Hike"],
  };
  const subStyleImages = {
    "Modern Office": "https://i.imgur.com/pxE5JH1.png",
    "Classic Portrait": "https://imgur.com/BTJUfN7.png",
    "Team Setting": "https://imgur.com/TqrnQSg.png",
    "Bright & Airy Office": "https://i.imgur.com/0Dt4PdC.png",
    "Urban Environment": "https://i.imgur.com/RKWnVui.png",
    "Casual Co-working": "https://i.imgur.com/8sjfLYo.png",
    "Art Studio": "https://i.imgur.com/3YPc30o.png",
    "Minimalist Background": "https://i.imgur.com/Ddxy1SV.png",
    "Bold Colors": "https://i.imgur.com/XIILJJl.png",
    "Indoor (Window Light)": "https://i.imgur.com/6ZTdd78.png",
    "Outdoor (Park)": "https://i.imgur.com/ApzeuKL.png",
    "Outdoor (Urban)": "https://i.imgur.com/mTVP7c8.png",
    "Cocktail Lounge": "https://i.imgur.com/COuYErF.png",
    "City Nightscape": "https://i.imgur.com/WP73kAe.png",
    "Elegant Restaurant": "https://i.imgur.com/t6EYC64.png",
    "European City": "https://i.imgur.com/B2gTzfA.png",
    "Tropical Beach": "https://imgur.com/T64sgjj.png",
    "Mountain Hike": "https://imgur.com/PxtctAq.png",
  };
  const poseOptions = {
    Corporate: [
      "Confident smile, arms crossed",
      "Leaning slightly forward",
      "Straight-on, warm smile",
    ],
    "Tech Start-up": [
      "Approachable laugh",
      "Leaning against a wall",
      "Casual, friendly look",
    ],
    Creative: [
      "Thoughtful expression",
      "Direct, intense gaze",
      "Slightly off-center pose",
    ],
    Natural: [
      "Relaxed, genuine smile",
      "Looking slightly away",
      "Candid laughing shot",
    ],
    "Going Out": [
      "Confident & poised",
      "Candid laughing",
      "Stylish over-the-shoulder look",
    ],
    Destination: [
      "Candid exploring",
      "Smiling with landmark",
      "Adventurous look",
    ],
  };
  const poseImages = {
    "Confident smile, arms crossed": "https://i.imgur.com/HMeBwmO.png",
    "Leaning slightly forward": "https://i.imgur.com/0jFarwP.png",
    "Straight-on, warm smile": "https://imgur.com/t0Q87xz.png",
    "Approachable laugh": "https://imgur.com/9eOXKLg.png",
    "Leaning against a wall": "https://imgur.com/sFjKWt7.png",
    "Casual, friendly look": "https://imgur.com/d318DT7.png",
    "Thoughtful expression": "https://imgur.com/fOHrBnh.png",
    "Direct, intense gaze": "https://imgur.com/ecoHhWD.png",
    "Slightly off-center pose": "https://imgur.com/muIA5YZ.png",
    "Relaxed, genuine smile": "https://imgur.com/ZEXKiSO.png",
    "Looking slightly away": "https://imgur.com/S7n2daZ.png",
    "Candid laughing shot": "https://imgur.com/ssNR1N7.png",
    "Confident & poised": "https://i.imgur.com/HNCjDQb.png",
    "Candid laughing": "https://i.imgur.com/gafqbAm.png",
    "Stylish over-the-shoulder look": "https://i.imgur.com/1Njmuxb.png",
    "Candid exploring": "https://imgur.com/7ctZuhS.png",
    "Smiling with landmark": "https://imgur.com/UIXKr9H.png",
    "Adventurous look": "https://imgur.com/eEZv78D.png",
  };
  const framingOptions = ["Close-up", "Medium Shot", "Wide Shot"];
  const framingImages = {
    "Close-up": "https://i.imgur.com/XOD1Mwz.png",
    "Medium Shot": "https://i.imgur.com/ngPYbmE.png",
    "Wide Shot": "https://i.imgur.com/0hKcKTu.png",
  };
  const aspectRatioOptions = {
    "Portrait (4:5)": "4:5",
    "Landscape (16:9)": "16:9",
  };
  const aspectRatioImages = {
    "4:5": "https://i.imgur.com/FQMzA7U.png",
    "16:9": "https://i.imgur.com/GMjOLEN.png",
  };
  const timeOfDayImages = {
    Day: "/img/day_75.webp",
    "Golden Hour": "/img/golden_75.webp",
    Night: "/img/night_75.webp",
  };
  const recommendedPresets = [
    {
      title: "Professional Portrait",
      description: "Clean office look with a confident pose.",
      settings: {
        selectedCategory: "Corporate",
        selectedSubCategory: "Modern Office",
        selectedPose: "Confident smile, arms crossed",
        selectedFraming: "Close-up",
        timeOfDay: "Day",
      },
    },
    {
      title: "Creative Studio",
      description: "Art-forward vibe with bold color.",
      settings: {
        selectedCategory: "Creative",
        selectedSubCategory: "Art Studio",
        selectedPose: "Thoughtful expression",
        selectedFraming: "Medium Shot",
        timeOfDay: "Golden Hour",
      },
    },
    {
      title: "Casual Tech",
      description: "Approachable, modern, and relaxed.",
      settings: {
        selectedCategory: "Tech Start-up",
        selectedSubCategory: "Casual Co-working",
        selectedPose: "Casual, friendly look",
        selectedFraming: "Medium Shot",
        timeOfDay: "Day",
      },
    },
    {
      title: "Outdoor Natural",
      description: "Soft lighting with a natural backdrop.",
      settings: {
        selectedCategory: "Natural",
        selectedSubCategory: "Outdoor (Park)",
        selectedPose: "Relaxed, genuine smile",
        selectedFraming: "Close-up",
        timeOfDay: "Golden Hour",
      },
    },
  ];
  const stepSummaries = {
    1: selectedFile?.name || "Upload a clear selfie",
    2: `${selectedCategory} · ${selectedSubCategory}`,
    3: selectedPose,
    4: selectedFraming,
    5: timeOfDay,
    6:
      clothingOption === "describe"
        ? "Describe new outfit"
        : clothingOption === "upload"
        ? "Upload new outfit"
        : "Keep original outfit",
  };
  const stepCompletion = {
    1: Boolean(originalBase64 || sourceImageUrl),
    2: Boolean(selectedCategory && selectedSubCategory),
    3: Boolean(selectedPose),
    4: Boolean(selectedFraming),
    5: Boolean(timeOfDay),
    6: Boolean(clothingOption),
  };
  const clothingSuggestions = {
    Corporate: {
      "Men's": [
        "Business Suit",
        "Blazer & Dress Shirt",
        "Dress Shirt & Tie",
        "Charcoal Grey Suit",
        "Navy Blue Blazer",
        "Pinstripe Suit",
        "Tweed Sports Coat",
        "Vest with Dress Shirt",
        "Cashmere V-Neck Sweater",
        "Professional Polo Shirt",
      ],
      "Women's": [
        "Business Suit",
        "Blazer & Blouse",
        "Modern Business Dress",
        "Pencil Skirt & Silk Blouse",
        "Sheath Dress with Cardigan",
        "Tailored Trousers",
        "High-Neck Blouse",
        "Professional Jumpsuit",
        "Turtleneck under a Blazer",
        "Chic Wrap Dress",
      ],
    },
    "Tech Start-up": {
      "Men's": [
        "Smart-Casual Blazer",
        "Stylish Hoodie",
        "Button-Down Shirt",
        "Designer T-Shirt & Jeans",
        "Henley Shirt & Flannel",
        "Bomber Jacket",
        "Quarter-Zip Pullover",
        "Chambray Shirt",
        "Fitted Polo & Chinos",
        "Crewneck Sweatshirt",
      ],
      "Women's": [
        "Smart-Casual Blazer",
        "Stylish Knit Top",
        "Button-Down Shirt",
        "Jeans & Silk Camisole",
        "Jumpsuit with Sneakers",
        "Midi Skirt & T-Shirt",
        "Boyfriend Blazer",
        "Cardigan & Ankle Pants",
        "Stylish Overalls",
        "A-line Skirt & Sweater",
      ],
    },
    Creative: {
      "Men's": [
        "Black Turtleneck",
        "Minimalist T-Shirt",
        "Casual Jacket",
        "Denim Jacket",
        "Linen Shirt",
        "Leather Jacket",
        "Chunky Knit Cardigan",
        "Band-Collar Shirt",
        "Artistic Graphic Sweatshirt",
        "Stylish Utility Vest",
      ],
      "Women's": [
        "Black Turtleneck",
        "Artistic Blouse",
        "Minimalist V-Neck",
        "Flowy Kimono",
        "Wide-Leg Trousers",
        "Vintage-style Dress",
        "Leather Jacket & Slip Dress",
        "Oversized Sweater",
        "Bold Patterned Jumpsuit",
        "Asymmetrical Top",
      ],
    },
    Natural: {
      "Men's": [
        "Knit Sweater",
        "Plain T-Shirt",
        "Linen Shirt",
        "Soft Flannel Shirt",
        "Henley Long-Sleeve",
        "Cable-Knit Crewneck",
        "Comfortable Polo Shirt",
        "V-neck sweater",
        "Chambray Button-Down",
        "Sherpa-lined Vest",
      ],
      "Women's": [
        "Knit Sweater",
        "Plain T-Shirt",
        "Linen Shirt",
        "Flowy Sundress",
        "Cozy Cardigan",
        "Simple Cotton Blouse",
        "Overalls with a T-shirt",
        "Soft oversized hoodie",
        "Tunic top with leggings",
        "Denim shirt dress",
      ],
    },
    "Going Out": {
      "Men's": [
        "Sharp Blazer & T-Shirt",
        "Camp-Collar Shirt",
        "Modern Bomber Jacket",
        "Fitted Dress Shirt",
        "Velvet Blazer",
        "Silk Button-Down",
        "Leather Biker Jacket",
        "Mandarin Collar Shirt",
        "Stylish Patterned Polo",
        "Slim-fit Turtleneck",
      ],
      "Women's": [
        "Chic Night-Out Attire",
        "Elegant Evening Wear",
        "Stylish Cocktail Dress",
        "Glamorous Gown",
        "Modern Club Attire",
        "Sequin Top & Pants",
        "Satin Slip Dress",
        "Off-the-Shoulder Bodysuit",
        "Little Black Dress",
        "Fashion-Forward Jumpsuit",
      ],
    },
    Destination: {
      "European City": {
        "Men's": [
          "Trench Coat",
          "Stylish Scarf",
          "Peacoat",
          "Fitted Sweater",
          "Casual Blazer",
          "Chinos",
          "Leather Loafers",
          "Button-down Shirt",
          "Lightweight Jacket",
          "Wool Trousers",
        ],
        "Women's": [
          "Chic Trench Coat",
          "Stylish Scarf",
          "Beret",
          "Midi Skirt & Blouse",
          "Elegant A-line Dress",
          "Ankle Boots",
          "Cashmere Sweater",
          "Tailored Coat",
          "Wide-Leg Trousers",
          "Crossbody Bag",
        ],
      },
      "Tropical Beach": {
        "Men's": [
          "Swim Trunks",
          "Board Shorts",
          "Linen Shirt (open)",
          "Hawaiian Shirt",
          "Tank Top",
          "Casual Flip-Flops",
          "Sunglasses",
          "Sun Hat",
          "Short-Sleeve Button-Down",
          "Lightweight Shorts",
        ],
        "Women's": [
          "One-Piece Swimsuit",
          "Bikini",
          "Sarong Wrap",
          "Kaftan Cover-Up",
          "Sundress",
          "Linen Pants & Top",
          "Romper",
          "High-Waisted Shorts",
          "Wide-Brimmed Sun Hat",
          "Flowy Maxi Dress",
        ],
      },
      "Mountain Hike": {
        "Men's": [
          "Performance Hiking Shirt",
          "Fleece Vest",
          "Waterproof Jacket",
          "Hiking Pants",
          "Puffer Vest",
          "Sun Hoodie",
          "Flannel Shirt",
          "Moisture-Wicking T-Shirt",
          "Cargo Shorts",
          "Beanie or Cap",
        ],
        "Women's": [
          "Athletic Hiking Top",
          "Fleece Jacket",
          "Waterproof Shell Jacket",
          "Hiking Leggings",
          "Puffer Vest",
          "Sun Hoodie",
          "Flannel Shirt",
          "Moisture-Wicking Tank",
          "Hiking Shorts",
          "Beanie or Headband",
        ],
      },
    },
  };
  const bodyShapeOptions = [
    "As in photo",
    "Slender",
    "Athletic",
    "Dad Bod",
    "Curvy",
    "Fuller Figure",
  ];
  const timeOfDayOptions = Object.keys(timeOfDayImages);

  // Moved the PremiumLockOverlay logic directly into the components to be locked
  const makeApiCall = async (endpoint, payload) => {
    if (!firebaseUser) {
      updateGeneratorState({
        error: "You must be logged in to perform this action.",
      });
      return null;
    }
    let attempt = 0;
    const maxAttempts = 3;
    const initialDelay = 1000;
    while (attempt < maxAttempts) {
      try {
        const token = await firebaseUser.getIdToken();
        const response = await fetch(`${cloudFunctionUrl}/${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Cloud Function Error:", response.status, errorData);
          throw new Error(
            errorData.error || `API responded with status: ${response.status}`
          );
        }
        const result = await response.json();
        return result;
      } catch (err) {
        console.error(`Attempt ${attempt + 1} failed:`, err);
        attempt++;
        if (attempt >= maxAttempts) {
          if (!generatorState.error) {
            updateGeneratorState({
              error:
                err.message ||
                "Failed to communicate with the server after multiple attempts.",
            });
          }
          return null;
        }
        await delay(initialDelay * Math.pow(2, attempt));
      }
    }
    return null;
  };

  const getTextFromImage = async (imageUrl, prompt) => {
    const payload = { imageUrl, prompt };
    const result = await makeApiCall("getTextFromImage", payload);
    if (result && result.text) {
      return result.text;
    } else {
      return "";
    }
  };

  const getClothingInstruction = async (settings) => {
    if (settings.clothingOption === "original") {
      return "The person's clothing, jewelry, and any accessories MUST be kept exactly as they appear in the original reference photo. Do not change the outfit.";
    }
    const defaultClothing = {
      Corporate: {
        "Men's":
          "professional men's business attire, such as a suit or blazer.",
        "Women's":
          "professional women's business attire, such as a suit or blouse.",
      },
      "Tech Start-up": {
        "Men's":
          "stylish men's smart-casual attire, like a fashionable jacket over a plain t-shirt.",
        "Women's":
          "stylish women's smart-casual attire, like a fashionable blazer or knit top.",
      },
      Creative: {
        "Men's":
          "artistic and modern men's clothing, like a minimalist sweater or jacket.",
        "Women's":
          "artistic and modern women's clothing, like a minimalist sweater or a unique blouse.",
      },
      Natural: {
        "Men's":
          "comfortable and stylish men's casual wear, like a high-quality knit top or a simple t-shirt.",
        "Women's":
          "comfortable and stylish women's casual wear, like a high-quality knit top or a simple, elegant t-shirt.",
      },
      "Going Out": {
        "Men's":
          "a sharp, stylish men's outfit suitable for a night out, like a blazer over a t-shirt or a fashionable shirt.",
        "Women's":
          "a chic and elegant women's outfit suitable for a night out, such as a stylish dress or sophisticated attire.",
      },
      Destination: {
        "European City": {
          "Men's":
            "stylish men's travel attire suitable for a European city, like a trench coat or a fitted sweater.",
          "Women's":
            "chic women's travel attire suitable for a European city, like a stylish coat or an elegant dress.",
        },
        "Tropical Beach": {
          "Men's": "men's beachwear, such as swim trunks or a linen shirt.",
          "Women's": "women's beachwear, such as a swimsuit or a sundress.",
        },
        "Mountain Hike": {
          "Men's":
            "men's hiking gear, like a fleece vest or performance shirt.",
          "Women's":
            "women's hiking gear, like an athletic top or waterproof jacket.",
        },
      },
    };
    const getDefaultClothingText = () => {
      if (settings.selectedCategory === "Destination") {
        return (
          defaultClothing.Destination[settings.selectedSubCategory]?.[
            settings.clothingGender
          ] || "appropriate travel attire."
        );
      }
      return (
        defaultClothing[settings.selectedCategory]?.[settings.clothingGender] ||
        "appropriate attire."
      );
    };
    if (settings.clothingOption === "upload" && settings.clothingBase64) {
      updateGeneratorState({ loadingMessage: "Analyzing clothing..." });
      const clothingDesc = await getTextFromImage(
        settings.clothingBase64,
        "Describe the clothing in this image in a concise phrase suitable for an AI image generator prompt."
      );
      return clothingDesc
        ? `The person should be wearing an outfit similar to: ${clothingDesc}.`
        : `The person should be wearing ${getDefaultClothingText()}`;
    }
    const defaultText = getDefaultClothingText();
    return settings.clothingStyle.trim()
      ? `The person should be wearing ${settings.clothingStyle.trim()}.`
      : `The person should be wearing ${defaultText}`;
  };
  const addWatermark = (base64Image, watermark) => {
    if (!watermark) {
      console.error("Watermark image has not been pre-loaded.");
      return base64Image;
    }
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const mainImage = new Image();
      mainImage.onload = () => {
        canvas.width = mainImage.width;
        canvas.height = mainImage.height;
        ctx.drawImage(mainImage, 0, 0);
        const watermarkSize = canvas.width * 0.1;
        const margin = canvas.width * 0.02;
        const x = canvas.width - watermarkSize - margin;
        const y = canvas.height - watermarkSize - margin;
        ctx.globalAlpha = 0.6;
        ctx.drawImage(watermark, x, y, watermarkSize, watermarkSize);
        ctx.globalAlpha = 1.0;
        resolve(canvas.toDataURL("image/png"));
      };
      mainImage.src = base64Image;
    });
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateGeneratorState({
          selectedFile: file,
          originalBase64: reader.result,
          sourceImageUrl: null,
          imagePreview: reader.result,
          error: null,
          generatedImage: null,
          cleanGeneratedImage: null,
          history: [],
        });
        setOpenStep(2);
        scrollToStep(2);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClothingFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !firebaseUser || !activeUserId) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      updateGeneratorState({
        clothingPreview: base64String,
        isLoading: true,
        loadingMessage: "Uploading...",
      });

      // Upload to Firebase Storage
      const storageRef = ref(
        storage,
        `user_uploads/${activeUserId}/clothing/${Date.now()}_${file.name}`
      );
      try {
        const snapshot = await uploadString(
          storageRef,
          base64String,
          "data_url"
        );
        const downloadURL = await getDownloadURL(snapshot.ref);
        updateGeneratorState({
          clothingFile: file,
          clothingBase64: downloadURL, // Store the URL instead of the base64 string
          isLoading: false,
          loadingMessage: "",
        });
      } catch (error) {
        console.error("Error uploading clothing file:", error);
        updateGeneratorState({
          error: "Failed to upload clothing image.",
          isLoading: false,
          loadingMessage: "",
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const saveImageToGallery = async (imageB64) => {
    if (!activeUserId || !imageB64) return;
    try {
      const storageRef = ref(
        storage,
        `user_uploads/${activeUserId}/gallery/${Date.now()}.png`
      );
      const snapshot = await uploadString(storageRef, imageB64, "data_url");
      const downloadURL = await getDownloadURL(snapshot.ref);

      const galleryCollectionRef = collection(
        db,
        "users",
        activeUserId,
        "gallery"
      );
      await addDoc(galleryCollectionRef, {
        imageUrl: downloadURL,
        imagePath: storageRef.fullPath,
        createdAt: serverTimestamp(),
        selectedCategory,
        selectedSubCategory,
        selectedPose,
        selectedFraming,
        timeOfDay,
        clothingOption,
        favorite: false,
      });
    } catch (error) {
      console.error("Error saving image to gallery:", error);
    }
  };

  const handleRemoveClothingImage = () => {
    updateGeneratorState({
      clothingFile: null,
      clothingBase64: null,
      clothingPreview: null,
    });
    const fileInput = document.getElementById("clothing-file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };
  const handleCategoryChange = (category) => {
    updateGeneratorState({
      selectedCategory: category,
      selectedSubCategory: styleOptions[category][0],
      selectedPose: poseOptions[category][0],
      clothingStyle: "",
    });
    setOpenStep(3);
    scrollToStep(3);
  };

  const handlePresetApply = (preset) => {
    updateGeneratorState({
      selectedCategory: preset.selectedCategory,
      selectedSubCategory: preset.selectedSubCategory,
      selectedPose: preset.selectedPose,
      selectedFraming: preset.selectedFraming,
      timeOfDay: preset.timeOfDay,
      clothingOption: "original",
      clothingStyle: "",
    });
    setOpenStep(6);
    scrollToStep(6);
  };

  const StepHeader = ({ step, title, summary }) => {
    const isOpen = openStep === step;
    const isComplete = stepCompletion[step];
    return (
      <button
        type="button"
        onClick={() => setOpenStep(step)}
        className={`w-full flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-left transition ${
          isOpen ? "bg-gray-900/60" : "bg-gray-900/30 hover:bg-gray-900/60"
        }`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
              isComplete
                ? "bg-green-500/20 text-green-300 border border-green-400/50"
                : "bg-gray-700 text-gray-200 border border-gray-600"
            }`}
          >
            {step}
          </span>
          <div>
            <p className="font-semibold text-gray-100">{title}</p>
            <p className="text-xs text-gray-400">{summary}</p>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
    );
  };
  const handleGenerate = async (options = {}) => {
    const { isRefinement = false, overrideSettings = null } = options;
    if ((!originalBase64 && !sourceImageUrl && !isRefinement) || isLoading)
      return;
    if (isRefinement && !generatedImage) {
      updateGeneratorState({
        error: "You must generate an image before you can refine it.",
      });
      return;
    }
    if ((user?.credits || 0) < 1) {
      updateGeneratorState({
        error: "You're out of credits! Please top up to continue creating.",
      });
      onNavigate("pricing");
      return;
    }
    updateGeneratorState({
      isLoading: true,
      showShareOptions: false,
      error: null,
    });

    let payload;

    if (isRefinement) {
      updateGeneratorState({ loadingMessage: "Refining image..." });
      const imageToProcess = cleanGeneratedImage;
      const img = new Image();
      img.src = imageToProcess;
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = img.width;
      maskCanvas.height = img.height;
      const maskCtx = maskCanvas.getContext("2d");
      maskCtx.fillStyle = "white";
      maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
      const maskToProcess = maskCanvas.toDataURL("image/jpeg");

      payload = {
        isRefinement: true,
        refinementPrompt,
        imageToProcess,
        maskToProcess,
      };
    } else {
      updateGeneratorState({ loadingMessage: "Generating..." });

      const currentSettings = overrideSettings || {
        selectedCategory,
        selectedSubCategory,
        selectedPose,
        selectedFraming,
        timeOfDay,
        clothingOption,
        clothingGender,
        clothingStyle,
        clothingBase64,
        bodyShape,
        styleStrength,
        negativePrompt,
        additionalDetails,
      };
      const clothingInstruction = await getClothingInstruction(currentSettings);

      if (sourceImageUrl && !originalBase64) {
        payload = {
          isRefinement: false,
          source_image_url: sourceImageUrl,
          settings: { ...currentSettings, clothingInstruction },
        };
      } else {
        payload = {
          isRefinement: false,
          imageToProcess: processedComposite,
          maskToProcess: processedMask,
          settings: { ...currentSettings, clothingInstruction },
        };
      }
    }

    const result = await makeApiCall("generateImage", payload);

    if (result && result.imageUrl) {
      if (activeUserId) {
        const userDocRef = doc(db, "users", activeUserId);
        try {
          await updateDoc(userDocRef, {
            images_generated: increment(1),
          });
        } catch (error) {
          console.error("Error incrementing images_generated count:", error);
          // Non-critical error, so we just log it and continue
        }
      }

      let rawImage = result.imageUrl;

      await saveImageToGallery(rawImage);

      let displayImage = rawImage;
      if (user.plan === "Free" && watermarkImage) {
        updateGeneratorState({ loadingMessage: "Adding watermark..." });
        displayImage = await addWatermark(rawImage, watermarkImage);
      }
      updateGeneratorState({
        generatedImage: displayImage,
        cleanGeneratedImage: rawImage,
        history: [{ clean: rawImage, display: displayImage }, ...history].slice(
          0,
          5
        ),
        refinementPrompt: "",
      });
      // Decrement credits on the frontend for immediate feedback
      onCreditUpdate(-1);
    } else {
      if (!generatorState.error)
        updateGeneratorState({
          error: "Failed during the generation step. Please try again.",
        });
    }
    updateGeneratorState({ isLoading: false, loadingMessage: "" });
  };

  const handleDownload = () => {
    if (!generatedImage) return;

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS) {
      // For iOS, open the image in a new tab for the user to save manually
      const newWindow = window.open(generatedImage, "_blank");
      if (newWindow) {
        newWindow.focus();
      } else {
        // This might happen if the browser blocks popups
        alert("Please allow popups for this site to save your image.");
      }
    } else {
      // Standard download method for Android/Desktop
      const link = document.createElement("a");
      link.href = generatedImage;
      link.download = "faceboom-headshot.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getClothingList = () => {
    if (selectedCategory === "Destination") {
      return (
        clothingSuggestions.Destination[selectedSubCategory]?.[
          clothingGender
        ] || []
      );
    }
    return clothingSuggestions[selectedCategory]?.[clothingGender] || [];
  };
  const handleSurpriseMe = async () => {
    if (!originalBase64 || isLoading) return;
    if ((user?.credits || 0) < 1) {
      updateGeneratorState({
        error:
          "You're out of credits! Please upgrade to generate a 'Surprise Me' image.",
      });
      onNavigate("pricing");
      return;
    }
    updateGeneratorState({
      isLoading: true,
      loadingMessage: "Randomizing style...",
    });
    const detectedGenderText = await getTextFromImage(
      originalBase64,
      "Analyze the person in this photo and state their likely gender as a single word: 'Man' or 'Woman'."
    );
    let detectedClothingGender = Math.random() < 0.5 ? "Women's" : "Men's";
    if (detectedGenderText) {
      if (detectedGenderText.toLowerCase().includes("woman")) {
        detectedClothingGender = "Women's";
      } else if (detectedGenderText.toLowerCase().includes("man")) {
        detectedClothingGender = "Men's";
      }
    }
    const randomCategory =
      Object.keys(styleOptions)[
        Math.floor(Math.random() * Object.keys(styleOptions).length)
      ];
    const randomSubCategory =
      styleOptions[randomCategory][
        Math.floor(Math.random() * styleOptions[randomCategory].length)
      ];
    const randomPose =
      poseOptions[randomCategory][
        Math.floor(Math.random() * poseOptions[randomCategory].length)
      ];
    const randomFraming =
      framingOptions[Math.floor(Math.random() * framingOptions.length)];
    const randomTime =
      timeOfDayOptions[Math.floor(Math.random() * timeOfDayOptions.length)];
    const randomClothingGender = detectedClothingGender;
    let randomClothingStyle = "";
    let suggestionsList = [];
    if (randomCategory === "Destination") {
      suggestionsList =
        clothingSuggestions.Destination[randomSubCategory]?.[
          randomClothingGender
        ] || [];
    } else {
      suggestionsList =
        clothingSuggestions[randomCategory]?.[clothingGender] || [];
    }
    if (suggestionsList.length > 0) {
      randomClothingStyle =
        suggestionsList[Math.floor(Math.random() * suggestionsList.length)];
    }
    updateGeneratorState({
      selectedCategory: randomCategory,
      selectedSubCategory: randomSubCategory,
      selectedPose: randomPose,
      selectedFraming: randomFraming,
      timeOfDay: randomTime,
      clothingOption: "describe",
      clothingGender: randomClothingGender,
      clothingStyle: randomClothingStyle,
    });
    setOpenStep(6);
    scrollToStep(6);
    handleRemoveClothingImage();
    const surpriseSettings = {
      selectedCategory: randomCategory,
      selectedSubCategory: randomSubCategory,
      selectedPose: randomPose,
      selectedFraming: randomFraming,
      timeOfDay: randomTime,
      clothingOption: "describe",
      clothingGender: randomClothingGender,
      clothingStyle: randomClothingStyle,
      clothingBase64: null,
      bodyShape,
      styleStrength,
      negativePrompt,
      additionalDetails,
    };
    await handleGenerate({
      isRefinement: false,
      overrideSettings: surpriseSettings,
    });
  };

  const handleSaveStyle = async () => {
    if (!newStyleName.trim() || !activeUserId) return;
    const currentSettings = {
      name: newStyleName.trim(),
      settings: {
        selectedCategory,
        selectedSubCategory,
        selectedPose,
        selectedFraming,
        timeOfDay,
        clothingStyle,
        clothingGender,
        clothingOption,
        aspectRatio,
        bodyShape,
        negativePrompt,
        styleStrength,
        additionalDetails,
      },
    };
    const newSavedStyles = [...savedStyles, currentSettings];

    const userDocRef = doc(db, "users", activeUserId);
    await updateDoc(userDocRef, { savedStyles: newSavedStyles });

    updateGeneratorState({
      savedStyles: newSavedStyles,
      showSaveModal: false,
      newStyleName: "",
    });
  };

  const handleLoadStyle = (styleToLoad) => {
    const { settings } = styleToLoad;
    updateGeneratorState({ ...settings });
  };

  const handleDeleteStyle = async (styleNameToDelete) => {
    if (!activeUserId) return;
    const newSavedStyles = savedStyles.filter(
      (style) => style.name !== styleNameToDelete
    );

    const userDocRef = doc(db, "users", activeUserId);
    await updateDoc(userDocRef, { savedStyles: newSavedStyles });

    updateGeneratorState({ savedStyles: newSavedStyles });
  };

  // Same content as before, just removed the redundant closing tags at the end
  return (
    <div className="bg-gray-900 text-white font-sans min-h-screen">
      <div className="flex flex-col min-h-screen">
        {showSaveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm text-gray-100">
              <h3 className="text-lg font-semibold mb-4">Save Current Style</h3>
              <input
                type="text"
                value={newStyleName}
                onChange={(e) =>
                  updateGeneratorState({ newStyleName: e.target.value })
                }
                placeholder="Enter a name for your style"
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg mb-4"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => updateGeneratorState({ showSaveModal: false })}
                  className="py-2 px-4 rounded-lg bg-gray-600 hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStyle}
                  className="py-2 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        <AppHeader
          user={user}
          firebaseUser={firebaseUser}
          onNavigate={onNavigate}
          onLogout={onLogout}
          supportAwaitingCount={supportAwaitingCount}
        />
        <div className="w-full max-w-2xl mx-auto bg-gray-800 rounded-2xl shadow-2xl p-6 md:p-8 mt-8 flex-grow">
          <main>
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-200 mb-3">
                Recommended Presets
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {recommendedPresets.map((preset) => (
                  <button
                    key={preset.title}
                    onClick={() => handlePresetApply(preset.settings)}
                    className="text-left p-4 rounded-xl border border-gray-700 bg-gray-900/50 hover:bg-gray-900/70 transition"
                  >
                    <p className="font-semibold text-gray-100">
                      {preset.title}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {preset.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between gap-2 text-xs text-gray-400">
                {[
                  "Upload",
                  "Style",
                  "Pose",
                  "Framing",
                  "Time",
                  "Clothing",
                ].map((label, index) => {
                  const step = index + 1;
                  return (
                    <div
                      key={label}
                      className="flex-1 flex flex-col items-center gap-2"
                    >
                      <span
                        className={`text-base sm:text-lg font-semibold ${
                          openStep >= step ? "text-purple-300" : "text-gray-500"
                        }`}
                      >
                        {step}
                      </span>
                      <span
                        className={`h-2 w-full rounded-full ${
                          openStep >= step
                            ? "bg-purple-500"
                            : "bg-gray-700"
                        }`}
                      />
                      <span className="font-medium text-center text-[11px] sm:text-xs">
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              className="mb-6"
              ref={(el) => {
                stepRefs.current[1] = el;
              }}
            >
              <StepHeader
                step={1}
                title="Upload a reference photo"
                summary={stepSummaries[1]}
              />
              {openStep === 1 && (
                <div className="mt-4">
                  <div className="mt-2 flex justify-center items-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md min-h-[178px]">
                    <div className="space-y-1 text-center">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="mx-auto h-32 w-auto object-contain"
                        />
                      ) : (
                        <MaterialSymbol
                          name="image_arrow_up"
                          className="mx-auto text-5xl text-gray-500"
                        />
                      )}
                      <div className="flex text-sm text-gray-400 justify-center">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-gray-700 rounded-md font-medium text-indigo-400 hover:text-indigo-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-gray-800 focus-within:ring-indigo-500 px-3 py-1"
                        >
                          <span>
                            {selectedFile ? "Change photo" : "Upload photo"}
                          </span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".png,.jpg,.jpeg,.gif"
                            onChange={handleFileChange}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        {selectedFile
                          ? selectedFile.name
                          : "PNG, JPG, GIF up to 10MB"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900/60 p-4 text-left">
                    <p className="text-sm font-semibold text-gray-200 mb-2">
                      Results quality tips
                    </p>
                    <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
                      <li>Face centered, eyes visible, and no heavy filters.</li>
                      <li>Use bright, even lighting with minimal shadows.</li>
                      <li>Keep your face at least 35% of the photo.</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            <div
              className="mb-6"
              ref={(el) => {
                stepRefs.current[2] = el;
              }}
            >
              <StepHeader
                step={2}
                title="Choose a style"
                summary={stepSummaries[2]}
              />
              {openStep === 2 && (
                <>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {Object.keys(styleOptions).map((category) => {
                      const premiumCategories = ["Going Out", "Destination"];
                      const isPremiumStyle = premiumCategories.includes(
                        category
                      );
                      const isDisabled = isPremiumStyle && !isPremium;

                      return (
                        <div
                          key={category}
                          className="relative group flex flex-col gap-2"
                        >
                          <img
                            src={styleImages[category]}
                            alt={`${category} style representation`}
                            className={`rounded-lg h-24 object-cover w-full ${
                              isDisabled ? "opacity-50" : ""
                            }`}
                            onError={(e) => {
                              e.target.src = `https://placehold.co/400x240/1f2937/9ca3af?text=${category}`;
                            }}
                          />
                          <button
                            onClick={() =>
                              !isDisabled && handleCategoryChange(category)
                            }
                            disabled={isDisabled}
                            className={`py-2 px-3 w-full rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                              selectedCategory === category
                                ? "bg-indigo-600 shadow-lg"
                                : "bg-gray-700 hover:bg-gray-600"
                            } ${
                              isDisabled ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            {category}
                          </button>
                          {isDisabled && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-max px-2 py-1 bg-gray-900 text-yellow-300 text-xs font-bold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 inline mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                              </svg>
                              Premium
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Sub-style:{" "}
                      <span className="font-bold text-gray-300">
                        {selectedCategory}
                      </span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {styleOptions[selectedCategory].map((subStyle) => (
                        <div key={subStyle} className="flex flex-col gap-2">
                          <img
                            src={subStyleImages[subStyle]}
                            alt={`${subStyle} sub-style representation`}
                            className="rounded-md h-16 object-cover w-full"
                            onError={(e) => {
                              e.target.src = `https://placehold.co/200x160/1f2937/9ca3af?text=${
                                subStyle.split(" ")[0]
                              }`;
                            }}
                          />
                        <button
                          onClick={() => {
                            updateGeneratorState({
                              selectedSubCategory: subStyle,
                            });
                            setOpenStep(3);
                            scrollToStep(3);
                          }}
                          className={`py-2 px-2 text-xs sm:text-sm rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 w-full ${
                            selectedSubCategory === subStyle
                              ? "bg-purple-600 shadow-md"
                                : "bg-gray-600 hover:bg-gray-500"
                            }`}
                          >
                            {subStyle}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div
              className="mb-6"
              ref={(el) => {
                stepRefs.current[3] = el;
              }}
            >
              <StepHeader
                step={3}
                title="Choose a pose"
                summary={stepSummaries[3]}
              />
              {openStep === 3 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Pose:{" "}
                    <span className="font-bold text-gray-300">
                      {selectedCategory}
                    </span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {poseOptions[selectedCategory].map((pose) => (
                      <div key={pose} className="flex flex-col gap-2">
                        <img
                          src={poseImages[pose]}
                          alt={`${pose} pose representation`}
                          className="rounded-md h-16 object-cover w-full"
                          onError={(e) => {
                            e.target.src = `https://placehold.co/200x160/1f2937/9ca3af?text=${
                              pose.split(" ")[0]
                            }`;
                          }}
                        />
                        <button
                          onClick={() => {
                            updateGeneratorState({ selectedPose: pose });
                            setOpenStep(4);
                            scrollToStep(4);
                          }}
                          className={`py-2 px-2 text-xs sm:text-sm rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 w-full ${
                            selectedPose === pose
                              ? "bg-purple-600 shadow-md"
                              : "bg-gray-600 hover:bg-gray-500"
                          }`}
                        >
                          {pose}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div
              className="mb-6"
              ref={(el) => {
                stepRefs.current[4] = el;
              }}
            >
              <StepHeader
                step={4}
                title="Choose a framing"
                summary={stepSummaries[4]}
              />
              {openStep === 4 && (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {framingOptions.map((frame) => (
                    <div key={frame} className="flex flex-col gap-2">
                      <img
                        src={framingImages[frame]}
                        alt={`${frame} framing representation`}
                        className="rounded-md h-64 object-cover w-full"
                        onError={(e) => {
                          e.target.src = `https://placehold.co/200x320/1f2937/9ca3af?text=${
                            frame.split(" ")[0]
                          }`;
                        }}
                      />
                        <button
                          onClick={() => {
                            updateGeneratorState({ selectedFraming: frame });
                            setOpenStep(5);
                            scrollToStep(5);
                          }}
                          className={`py-2 px-2 text-xs sm:text-sm rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 w-full ${
                            selectedFraming === frame
                            ? "bg-purple-600 shadow-md"
                            : "bg-gray-600 hover:bg-gray-500"
                        }`}
                      >
                        {frame}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              className="mb-6"
              ref={(el) => {
                stepRefs.current[5] = el;
              }}
            >
              <StepHeader
                step={5}
                title="Choose time of day"
                summary={stepSummaries[5]}
              />
              {openStep === 5 && (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {timeOfDayOptions.map((time) => (
                    <div key={time} className="flex flex-col gap-2">
                      <img
                        src={timeOfDayImages[time]}
                        alt={`${time} time of day representation`}
                        className="rounded-md h-24 object-cover w-full"
                        onError={(e) => {
                          e.target.src = `https://placehold.co/200x160/1f2937/9ca3af?text=${time}`;
                        }}
                      />
                        <button
                          onClick={() => {
                            updateGeneratorState({ timeOfDay: time });
                            setOpenStep(6);
                            scrollToStep(6);
                          }}
                          className={`py-2 px-2 text-xs sm:text-sm rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 w-full ${
                            timeOfDay === time
                            ? "bg-purple-600 shadow-md"
                            : "bg-gray-600 hover:bg-gray-500"
                        }`}
                      >
                        {time}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              className="mb-6"
              ref={(el) => {
                stepRefs.current[6] = el;
              }}
            >
              <StepHeader
                step={6}
                title="Clothing & accessories"
                summary={stepSummaries[6]}
              />
              {openStep === 6 && (
                <>
                  <div className="mt-4 grid grid-cols-3 gap-2 mb-4">
                    <button
                      onClick={() =>
                        updateGeneratorState({ clothingOption: "original" })
                      }
                      className={`py-2 px-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                        clothingOption === "original"
                          ? "bg-indigo-600 shadow-lg"
                          : "bg-gray-700 hover:bg-gray-600"
                      }`}
                    >
                      Keep Original
                    </button>
                    <div className="relative group">
                      <button
                        onClick={() =>
                          isPremium &&
                          updateGeneratorState({ clothingOption: "describe" })
                        }
                        disabled={!isPremium}
                        className={`w-full py-2 px-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                          clothingOption === "describe"
                            ? "bg-indigo-600 shadow-lg"
                            : "bg-gray-700 hover:bg-gray-600"
                        } ${!isPremium ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        Describe New
                      </button>
                      {!isPremium && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-900 text-yellow-300 text-xs font-bold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 inline mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                          </svg>
                          Premium
                        </div>
                      )}
                    </div>
                    <div className="relative group">
                      <button
                        onClick={() =>
                          isPremium &&
                          updateGeneratorState({ clothingOption: "upload" })
                        }
                        disabled={!isPremium}
                        className={`w-full py-2 px-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                          clothingOption === "upload"
                            ? "bg-indigo-600 shadow-lg"
                            : "bg-gray-700 hover:bg-gray-600"
                        } ${!isPremium ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        Upload New
                      </button>
                      {!isPremium && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-900 text-yellow-300 text-xs font-bold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 inline mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                          </svg>
                          Premium
                        </div>
                      )}
                    </div>
                  </div>
                  {clothingOption === "describe" && isPremium && (
                    <div className="p-4 bg-gray-900/50 rounded-lg animate-fade-in">
                      <label
                        htmlFor="clothing"
                        className="block text-sm font-medium text-gray-300 mb-2"
                      >
                        Describe clothing style
                      </label>
                      <input
                        id="clothing"
                        type="text"
                        value={clothingStyle}
                        onChange={(e) =>
                          updateGeneratorState({
                            clothingStyle: e.target.value,
                          })
                        }
                        placeholder="e.g., 'a black turtleneck' or 'a blue blazer'"
                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300"
                      />
                      <div className="flex gap-2 my-3">
                        <button
                          onClick={() =>
                            updateGeneratorState({ clothingGender: "Women's" })
                          }
                          className={`w-full py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${
                            clothingGender === "Women's"
                              ? "bg-purple-600 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                        >
                          Women's Style
                        </button>
                        <button
                          onClick={() =>
                            updateGeneratorState({ clothingGender: "Men's" })
                          }
                          className={`w-full py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${
                            clothingGender === "Men's"
                              ? "bg-purple-600 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                        >
                          Men's Style
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {getClothingList().map((suggestion) => (
                          <button
                            key={suggestion}
                            onClick={() =>
                              updateGeneratorState({
                                clothingStyle: suggestion,
                              })
                            }
                            className={`py-1 px-3 text-xs rounded-full font-semibold transition-colors duration-200 ${
                              clothingStyle === suggestion
                                ? "bg-purple-600 text-white"
                                : "bg-gray-600 text-gray-300 hover:bg-gray-500"
                            }`}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {clothingOption === "upload" && isPremium && (
                    <div className="p-4 bg-gray-900/50 rounded-lg animate-fade-in">
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Upload clothing reference
                      </label>
                      <div className="mt-2 flex justify-center items-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          {clothingPreview ? (
                            <div className="relative mx-auto w-24 h-24 flex items-center justify-center">
                              <img
                                src={clothingPreview}
                                alt="Clothing Preview"
                                className="max-h-full max-w-full object-contain"
                              />
                              <button
                                onClick={handleRemoveClothingImage}
                                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 leading-none hover:bg-red-700 transition-colors z-10"
                                title="Remove image"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <svg
                              className="mx-auto h-10 w-10 text-gray-500"
                              stroke="none"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                d="M19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h7a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21ZM22.71,4.29l-3-3a1,1,0,0,0-.33-.21,1,1,0,0,0-.76,0,1,1,0,0,0-.33.21l-3,3a1,1,0,0,0,1.42,1.42L18,4.41V10a1,1,0,0,0,2,0V4.41l1.29,1.3a1,1,0,0,0,1.42,0A1,1,0,0,0,22.71,4.29Z"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          <div className="flex text-sm text-gray-400 justify-center">
                            <label
                              htmlFor="clothing-file-upload"
                              className="relative cursor-pointer bg-gray-700 rounded-md font-medium text-indigo-400 hover:text-indigo-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-gray-800 focus-within:ring-indigo-500 px-3 py-1"
                            >
                              <span>
                                {clothingFile
                                  ? "Change clothes"
                                  : "Upload clothes"}
                              </span>
                              <input
                                id="clothing-file-upload"
                                name="file-upload"
                                type="file"
                                className="sr-only"
                                accept=".png,.jpg,.jpeg,.gif"
                                onChange={handleClothingFileChange}
                              />
                            </label>
                          </div>
                          <p className="text-xs text-gray-500">
                            {clothingFile
                              ? clothingFile.name
                              : "PNG, JPG, GIF up to 10MB"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="mb-6 relative">
              <div
                className={`${
                  !isPremium ? "opacity-40 blur-sm pointer-events-none" : ""
                }`}
              >
                <button
                  onClick={() =>
                    updateGeneratorState({ isAdvancedOpen: !isAdvancedOpen })
                  }
                  className="w-full text-left text-sm font-medium text-gray-300 mb-2 flex justify-between items-center"
                >
                  <span>7. Advanced Settings</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 transition-transform ${
                      isAdvancedOpen ? "transform rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isAdvancedOpen && (
                  <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Aspect Ratio
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(aspectRatioOptions).map(
                          ([name, value]) => (
                            <div key={value} className="flex flex-col gap-2">
                              <img
                                src={aspectRatioImages[value]}
                                alt={`${name} aspect ratio representation`}
                                className={`rounded-md w-full object-cover ${
                                  value === "4:5"
                                    ? "aspect-[4/5]"
                                    : "aspect-[16/9]"
                                }`}
                                onError={(e) => {
                                  e.target.src = `https://placehold.co/400x240/1f2937/9ca3af?text=${name}`;
                                }}
                              />
                              <button
                                onClick={() =>
                                  updateGeneratorState({ aspectRatio: value })
                                }
                                className={`py-2 px-2 text-xs sm:text-sm rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 w-full ${
                                  aspectRatio === value
                                    ? "bg-purple-600 shadow-md"
                                    : "bg-gray-600 hover:bg-gray-500"
                                }`}
                              >
                                {name}
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Body Shape
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {bodyShapeOptions.map((shape) => (
                          <button
                            key={shape}
                            onClick={() =>
                              updateGeneratorState({ bodyShape: shape })
                            }
                            className={`py-2 px-2 text-xs sm:text-sm rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 w-full ${
                              bodyShape === shape
                                ? "bg-purple-600 shadow-md"
                                : "bg-gray-600 hover:bg-gray-500"
                            }`}
                          >
                            {shape}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="styleStrength"
                        className="block text-sm font-medium text-gray-400 mb-2"
                      >
                        Style Strength:{" "}
                        <span className="font-bold text-gray-300">
                          {styleStrength}%
                        </span>
                      </label>
                      <input
                        id="styleStrength"
                        type="range"
                        min="10"
                        max="100"
                        value={styleStrength}
                        onChange={(e) =>
                          updateGeneratorState({
                            styleStrength: e.target.value,
                          })
                        }
                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="negativePrompt"
                        className="block text-sm font-medium text-gray-400 mb-2"
                      >
                        Negative Prompt
                      </label>
                      <input
                        id="negativePrompt"
                        type="text"
                        value={negativePrompt}
                        onChange={(e) =>
                          updateGeneratorState({
                            negativePrompt: e.target.value,
                          })
                        }
                        placeholder="e.g., hats, glasses, blurry background"
                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="additionalDetails"
                        className="block text-sm font-medium text-gray-400 mb-2"
                      >
                        Additional Details
                      </label>
                      <textarea
                        id="additionalDetails"
                        value={additionalDetails}
                        onChange={(e) =>
                          updateGeneratorState({
                            additionalDetails: e.target.value,
                          })
                        }
                        placeholder="e.g., remove people in the background, add a subtle lens flare, change hair color to brown"
                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300 h-24 resize-none"
                      />
                    </div>
                    <div className="pt-4 border-t border-gray-700/50">
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Saved Styles
                      </label>
                      {savedStyles.length > 0 ? (
                        <div className="space-y-2">
                          {savedStyles.map((style) => (
                            <div
                              key={style.name}
                              className="flex items-center justify-between bg-gray-700 p-2 rounded-lg"
                            >
                              <span className="text-sm font-medium">
                                {style.name}
                              </span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleLoadStyle(style)}
                                  className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-1 px-3 rounded-md"
                                >
                                  Load
                                </button>
                                <button
                                  onClick={() => handleDeleteStyle(style.name)}
                                  className="text-xs bg-red-600 hover:bg-red-500 text-white font-semibold py-1 px-3 rounded-md"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-2">
                          You haven't saved any styles yet.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {!isPremium && (
                <div className="absolute inset-0 bg-gray-800 bg-opacity-80 flex flex-col items-center justify-center text-center p-4 rounded-lg z-10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-yellow-400 mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <p className="font-bold text-white">Premium Feature</p>
                  <p className="text-sm text-gray-300 mb-3">
                    Upgrade to unlock this and more.
                  </p>
                  <button
                    onClick={() => onNavigate("pricing")}
                    className="bg-yellow-500 text-gray-900 font-bold py-2 px-4 rounded-lg shadow-lg hover:bg-yellow-400 transition text-sm"
                  >
                    Upgrade Now
                  </button>
                </div>
              )}
            </div>

            {/* REFACTORED GENERATE AREA */}
            <div className="mt-8 pt-6 border-t border-gray-700/50">
              <h2 className="text-2xl font-bold text-center mb-6">
                Generate Your Portrait
              </h2>
              <div className="mb-4 rounded-lg border border-gray-700 bg-gray-900/50 px-4 py-3 text-sm text-gray-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>
                  Estimated time:{" "}
                  <span className="font-semibold text-gray-100">
                    30–60 seconds
                  </span>
                </span>
                <span>
                  Cost per generation:{" "}
                  <span className="font-semibold text-gray-100">1 credit</span>
                  <span className="text-gray-500 ml-2">
                    (You have {user?.credits || 0})
                  </span>
                </span>
              </div>
              <div className="space-y-4">
                <div className="flex justify-center">
                  <button
                    onClick={() =>
                      (user?.credits || 0) > 0
                        ? handleGenerate({ isRefinement: false })
                        : onNavigate("pricing")
                    }
                    disabled={isLoading || (!originalBase64 && !sourceImageUrl)}
                    className={`w-full font-bold py-4 px-10 text-lg rounded-lg shadow-xl transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto 
                                ${
                                  (user?.credits || 0) > 0
                                    ? "bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
                                    : "bg-yellow-500 hover:bg-yellow-600 text-gray-900"
                                }`}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {loadingMessage || "Generating..."}
                      </>
                    ) : (user?.credits || 0) > 0 ? (
                      <>
                        <MaterialSymbol
                          name="wand_stars"
                          className="mr-2 text-xl"
                        />
                        Generate
                      </>
                    ) : (
                      <>
                        <MaterialSymbol
                          name="offline_bolt"
                          className="mr-2 text-xl"
                        />
                        Get More Credits
                      </>
                    )}
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleSurpriseMe}
                    disabled={isLoading || !originalBase64}
                    className="w-full sm:w-auto bg-gray-600 text-white font-bold py-3 px-6 rounded-lg shadow-xl hover:bg-gray-500 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                  >
                    <MaterialSymbol
                      name="sentiment_excited"
                      className="mr-2 text-xl"
                    />
                    Surprise Me!
                  </button>
                  <div className="relative group">
                    <button
                      onClick={() =>
                        isPremium &&
                        updateGeneratorState({ showSaveModal: true })
                      }
                      disabled={!isPremium || isLoading}
                      className="w-full sm:w-auto bg-gray-600 text-white font-bold py-3 px-6 rounded-lg shadow-xl hover:bg-gray-500 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                    >
                      <MaterialSymbol
                        name="bookmark_add"
                        className="mr-2 text-xl"
                      />
                      Save Style
                    </button>
                    {!isPremium && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-900 text-yellow-300 text-xs font-bold rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 inline mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        Premium
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* The monetization buttons below are now logically separated */}
              <div className="pt-4 mt-2 border-t border-gray-700/50 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => onNavigate("pricing")}
                  className="w-full sm:w-auto bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg shadow-xl hover:bg-indigo-500 transition duration-300 transform hover:scale-105 text-sm flex items-center justify-center gap-2"
                >
                  <MaterialSymbol name="offline_bolt" className="text-lg" />
                  Get More Credits
                </button>
                {user?.plan === "Free" && (
                  <button
                    onClick={() => onNavigate("pricing")}
                    className="w-full sm:w-auto bg-yellow-500 text-gray-900 font-bold py-2 px-6 rounded-lg shadow-xl hover:bg-yellow-400 transition duration-300 transform hover:scale-105 text-sm"
                  >
                    Go Premium
                  </button>
                )}
              </div>
            </div>
            {/* End of REFACTORED GENERATE AREA */}
          </main>
          <div className="mt-8 text-center">
            {error && (
              <p className="text-red-400 bg-red-900/50 p-3 rounded-lg">
                {error}
              </p>
            )}
            {generatedImage && !error && (
              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-4 text-gray-300">
                  Your Generated Headshot
                </h3>
                <div className="relative">
                  <img
                    src={generatedImage}
                    alt="Generated AI Headshot"
                    className="rounded-lg shadow-2xl mx-auto w-full h-auto border-4 border-gray-700 max-w-lg"
                  />
                </div>
                <div className="mt-4 flex justify-center gap-4 relative">
                  <button
                    onClick={handleDownload}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Save
                  </button>
                  <button
                    onClick={() =>
                      updateGeneratorState({
                        showShareOptions: !showShareOptions,
                      })
                    }
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                      />
                    </svg>
                    Share
                  </button>
                  {showShareOptions && (
                    <div className="absolute bottom-full mb-2 flex gap-2 bg-gray-900 p-2 rounded-lg shadow-2xl">
                      <a
                        href={`https://twitter.com/intent/tweet?text=Check%20out%20my%20new%20AI%20portrait%20from%20faceboom!&url=${window.location.href}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-700 rounded-full hover:bg-gray-600"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-700 rounded-full hover:bg-gray-600"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4z" />
                        </svg>
                      </a>
                      <a
                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=My%20New%20AI%20Portrait&summary=Check%20out%20my%20new%20AI%20portrait%20from%20faceboom!`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-700 rounded-full hover:bg-gray-600"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M6.5 21.5h-5v-13h5v13zM4 6.5C2.5 6.5 1.5 5.3 1.5 4s1-2.4 2.5-2.4c1.6 0 2.5 1 2.6 2.5 0 1.4-1 2.5-2.6 2.5zm11.5 6c-1 0-2 1-2 2v7h-5v-13h5V10s1.6-1.5 4-1.5c3 0 5 2.2 5 6.3v6.7h-5v-7c0-1-1-2-2-2z" />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
                <div className="mt-6 p-4 bg-gray-700/50 rounded-lg relative">
                  <div
                    className={`${
                      !isPremium ? "opacity-40 blur-sm pointer-events-none" : ""
                    }`}
                  >
                    <label
                      htmlFor="refinementPrompt"
                      className="block text-sm font-medium text-gray-300 mb-2"
                    >
                      Fine-tune your image
                    </label>
                    <textarea
                      id="refinementPrompt"
                      value={refinementPrompt}
                      onChange={(e) =>
                        updateGeneratorState({
                          refinementPrompt: e.target.value,
                        })
                      }
                      placeholder="e.g., 'change the shirt color to red', 'add a subtle smile', 'make hair slightly shorter'"
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300 h-20 resize-none"
                    />
                    <button
                      onClick={() =>
                        (user?.credits || 0) > 0
                          ? handleGenerate({ isRefinement: true })
                          : onNavigate("pricing")
                      }
                      disabled={isLoading || !refinementPrompt.trim()}
                      className={`mt-3 w-full font-bold py-2 px-6 rounded-lg shadow-md transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                        (user?.credits || 0) > 0
                          ? "bg-indigo-500 hover:bg-indigo-600 text-white"
                          : "bg-yellow-500 hover:bg-yellow-600 text-gray-900"
                      }`}
                    >
                      {isLoading && loadingMessage === "Refining image..." ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Refining...
                        </>
                      ) : (user?.credits || 0) > 0 ? (
                        "Refine"
                      ) : (
                        <>
                          <MaterialSymbol
                            name="offline_bolt"
                            className="mr-2 text-lg"
                          />
                          Get More Credits
                        </>
                      )}
                    </button>
                  </div>
                  {!isPremium && (
                    <div className="absolute inset-0 bg-gray-800 bg-opacity-80 flex flex-col items-center justify-center text-center p-4 rounded-lg z-10">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-yellow-400 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      <p className="font-bold text-white">Premium Feature</p>
                      <p className="text-sm text-gray-300 mb-3">
                        Upgrade to unlock this and more.
                      </p>
                      <button
                        onClick={() => onNavigate("pricing")}
                        className="bg-yellow-500 text-gray-900 font-bold py-2 px-4 rounded-lg shadow-lg hover:bg-yellow-400 transition text-sm"
                      >
                        Upgrade Now
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!generatedImage && !isLoading && !error && (
              <div className="text-gray-500 mt-6 p-8 border-2 border-dashed border-gray-700 rounded-lg">
                <p>Your generated headshot will appear here.</p>
              </div>
            )}
          </div>
          {history.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-center mb-4 text-gray-300">
                Session History
              </h3>
              <div className="flex justify-center gap-2 p-2 bg-gray-900/50 rounded-lg">
                <div
                  onClick={() => onNavigate("gallery")}
                  className="w-16 h-16 flex flex-col items-center justify-center text-center bg-gray-700 rounded-md cursor-pointer border-2 border-transparent hover:border-purple-500 transition-all text-white"
                  title="View Full Gallery"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 mb-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  <span className="text-xs font-semibold leading-tight">
                    Gallery
                  </span>
                </div>
                {history.map((historyItem, index) => (
                  <img
                    key={index}
                    src={historyItem.display}
                    alt={`History item ${index + 1}`}
                    className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 transition-all ${
                      generatedImage === historyItem.display
                        ? "border-purple-500 scale-110"
                        : "border-transparent hover:border-gray-500"
                    }`}
                    onClick={() =>
                      updateGeneratorState({
                        generatedImage: historyItem.display,
                        cleanGeneratedImage: historyItem.clean,
                      })
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- MODAL FOR LEGAL INFO & CONTACT ---
function LegalModal({ type, onClose }) {
  const getContent = () => {
    switch (type) {
      case "contact":
        return { title: "Contact Us", content: <ContactForm /> };
      case "terms":
        return {
          title: "Terms of Service",
          content: (
            <div className="text-sm text-gray-400 space-y-4">
              <p>
                <strong>Last Updated: September 8, 2025</strong>
              </p>
              <p>
                Welcome to FaceBoom! These Terms of Service ("Terms") govern
                your use of our AI-powered headshot generation services (the
                "Service"). By using our Service, you agree to these Terms.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                1. The Service
              </h3>
              <p>
                FaceBoom provides users with credits to generate digital
                portraits based on images they upload. Users can purchase
                credits and use them to access the generation features of the
                application.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                2. User Accounts & Credits
              </h3>
              <p>
                You may need to create an account to use our Service. You are
                responsible for all activities that occur under your account.
                Credits are non-refundable and must be used in accordance with
                our pricing policies. Unused credits do not expire.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                3. User Content
              </h3>
              <p>
                You retain all rights to the images you upload ("User Content").
                By uploading an image, you grant FaceBoom a non-exclusive,
                worldwide, royalty-free license to use, reproduce, and modify
                your User Content solely for the purpose of providing and
                improving the Service. You warrant that you have the necessary
                rights to grant us this license and that your content does not
                violate any laws or third-party rights.
              </p>
              <p>
                We do not claim ownership of the images generated by the AI. You
                are free to use them for any personal or commercial purpose,
                subject to the laws of your jurisdiction.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                4. Prohibited Use
              </h3>
              <p>
                You agree not to use the Service to create any harmful, abusive,
                hateful, or obscene content. This includes, but is not limited
                to, generating images that are defamatory, discriminatory, or
                that infringe on the privacy or intellectual property rights of
                others. We reserve the right to terminate accounts that violate
                this policy.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                5. Disclaimers
              </h3>
              <p>
                The Service is provided "as is" without any warranties of any
                kind. The AI-generated images may contain artifacts or
                inaccuracies. FaceBoom is not responsible for any consequences
                arising from the use of the generated images.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                6. Changes to Terms
              </h3>
              <p>
                We may modify these Terms at any time. We will notify you of any
                changes by posting the new Terms on this page. Your continued
                use of the Service after the changes take effect constitutes
                your acceptance of the new Terms.
              </p>
            </div>
          ),
        };
      case "privacy":
        return {
          title: "Privacy Policy",
          content: (
            <div className="text-sm text-gray-400 space-y-4">
              <p>
                <strong>Last Updated: September 8, 2025</strong>
              </p>
              <p>
                Your privacy is important to us. This Privacy Policy explains
                how FaceBoom ("we," "us," or "our") collects, uses, and
                discloses your information.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                1. Information We Collect
              </h3>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>
                  <strong>Account Information:</strong> When you create an
                  account, we collect your email address.
                </li>
                <li>
                  <strong>Uploaded Images:</strong> We temporarily process the
                  images you upload to provide the Service. These images are
                  used as a reference for the AI model.
                </li>
                <li>
                  <strong>Generated Images:</strong> We store the images
                  generated by our service to display them in your history.
                </li>
                <li>
                  <strong>Usage Data:</strong> We collect data on how you
                  interact with our Service to help us improve it.
                </li>
              </ul>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                2. How We Use Your Information
              </h3>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>To provide, maintain, and improve our Service.</li>
                <li>To process transactions and manage your account.</li>
                <li>
                  To communicate with you about your account or our services.
                </li>
                <li>
                  To monitor for and prevent fraudulent or prohibited activity.
                </li>
              </ul>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                3. Data Storage and Deletion
              </h3>
              <p>
                Uploaded reference images and generated masks are processed
                in-memory or stored temporarily and are deleted from our systems
                shortly after the generation process is complete. Generated
                headshots are stored to be displayed in your user history. You
                can delete your generated images from your history at any time,
                which will remove them from our active storage.
              </p>
              <p>We do not use your personal photos to train our AI models.</p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                4. Third-Party Services
              </h3>
              <p>
                We use third-party services, such as Google Gemini, for AI image
                processing. These services have their own privacy policies, and
                we recommend you review them. We only share the data necessary
                to provide the core functionality of our Service.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                5. Your Rights
              </h3>
              <p>
                You have the right to access, update, or delete your personal
                information. If you wish to delete your account and all
                associated data, please contact us through the Contact form.
              </p>

              <h3 className="font-bold text-gray-200 text-base pt-2">
                6. Security
              </h3>
              <p>
                We take reasonable measures to protect your information from
                unauthorized access or disclosure. However, no method of
                transmission over the internet is 100% secure.
              </p>
            </div>
          ),
        };
      default:
        return { title: "", content: null };
    }
  };
  const { title, content } = getContent();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto">{content}</div>
      </div>
    </div>
  );
}
